import { useNavigate } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Loader2, Search } from 'lucide-react';
import React, {
    type ReactElement,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    type AssistantPromptAction,
    DEFAULT_SHIFT_ACTION_POLICY,
    type ShiftActionPolicy,
    type ShiftTab,
} from '@apps/web/components/phoebe_chat/assistant_response_types';
import { usePhoebeChatOrganizationScope } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import {
    buildTranscriptFromPhoebeEvents,
    getLatestLiveAssistantMessageId,
    getLatestLiveThoughtId,
    hasPendingRecommendationBatch,
} from '@apps/web/components/phoebe_chat/event_transcript';
import {
    getMonitoringToolNodesFromRun,
    type MonitoringActivityControls,
    MonitoringConfigurationCard,
} from '@apps/web/components/phoebe_chat/monitoring_configuration_card';
import {
    getNumberValue,
    getStringValue,
    isRecord,
} from '@apps/web/components/phoebe_chat/record_utils';
import { EMPTY_SLACK_MENTION_MAP } from '@apps/web/components/phoebe_chat/slack_mentions';
import {
    type LiveRunStatus,
    type PhoebeAgentRunEventRead,
    type PhoebeAgentRunPendingEventRead,
    type StreamRunNode,
    type StreamRunState,
    type SubagentDraftProgress,
} from '@apps/web/components/phoebe_chat/streaming_types';
import { type QuestionAnswerPayload } from '@apps/web/components/phoebe_chat/user_question_model';
import { SaveNoteArtifactCard } from '@apps/web/components/phoebe_chat_view/artifacts/save_note_artifact_card';
import { StructuredReportArtifactPill } from '@apps/web/components/phoebe_chat_view/artifacts/structured_report_artifacts_panel';
import { UpdatePlaybookArtifactCard } from '@apps/web/components/phoebe_chat_view/artifacts/update_playbook_artifact_card';
import {
    processContentForMarkdown,
    renderReferenceAwareAnchor,
} from '@apps/web/components/phoebe_chat_view/assistant_responses/reference_markdown';
import { PhoebeAgentMark } from '@apps/web/components/phoebe_chat_view/brand_marks';
import { getDynamicWidgetActionStateForShifts } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_actions';
import {
    buildDynamicWidget,
    DynamicWidgetFallbackMetric,
    renderDynamicWidget,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_registry';
import {
    DynamicWidgetActionStateContext,
    DynamicWidgetPromptActionContext,
    useDynamicWidgetActionStateResolver,
    useDynamicWidgetBuildContext,
    useDynamicWidgetPromptAction,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widgets_context';
import { MessageBubble } from '@apps/web/components/phoebe_chat_view/messages/message_bubble';
import { LiveStatusRow } from '@apps/web/components/phoebe_chat_view/messages/status_rows';
import { RunDebriefAcknowledgement } from '@apps/web/components/phoebe_chat_view/run_debrief_card';
import {
    deriveLiveStatusFromRun,
    hasDeclaredExecutionPlanItems,
    hasRenderableLiveRunTreeContent,
    hasVisibleGeneralProgressRunContent,
    isLiveRunTreeDisplayNode,
    isLiveRunTreeRenderableNode,
} from '@apps/web/components/phoebe_chat_view/run_status/live_status';
import {
    type ProjectedRunMilestoneItem,
    type ProjectedRunMilestoneReceipt,
    ProjectedRunMilestones,
} from '@apps/web/components/phoebe_chat_view/run_status/projected_run_milestones';
import {
    derivePhoebeShimmerLabel,
    derivePhoebeThinkingTip,
    hasIncompleteCaregiverDecisionTool,
    isCaregiverDecisionToolName,
    PhoebeShimmerStatus,
} from '@apps/web/components/phoebe_chat_view/run_status/shimmer_status';
import {
    buildActivityTimelineSegments,
    buildPhoebeAgentRenderedTimelineEntries,
    getInterstitialAssistantMessageIds,
    summarizeActivityGroup,
} from '@apps/web/components/phoebe_chat_view/timeline/build_timeline_entries';
import {
    CompactionMarker,
    DebugEventGroupMarker,
    DebugEventMarker,
    LifecycleChip,
    parseRecommendationBatchAggregate,
    SubagentResultMarker,
} from '@apps/web/components/phoebe_chat_view/timeline/markers';
import {
    getToolApprovalBadge,
    getToolExecutionHeaderDetail,
    getToolExecutionReviewMessage,
    getToolResultReviewMessage,
} from '@apps/web/components/phoebe_chat_view/tool_calls/header_details';
import {
    ExecutionPlanChecklist,
    type ExecutionPlanChecklistStatus,
    humanizeReportToolName,
    type NormalizedDeclaredExecutionPlanFanInState,
    normalizeDeclaredExecutionPlanItems,
} from '@apps/web/components/phoebe_chat_view/tool_calls/infrastructure_tool_ui';
import { QueryToolRow } from '@apps/web/components/phoebe_chat_view/tool_calls/query_tool_ui';
import {
    RecommendationDecisionCarousel,
    RecommendationFanoutGroupProvider,
} from '@apps/web/components/phoebe_chat_view/tool_calls/recommendation_decision_carousel';
import { SKILL_CONTEXT_TOOL_NAMES } from '@apps/web/components/phoebe_chat_view/tool_calls/skill_context_tool_ui';
import { SurfaceOrganizationSettingCard } from '@apps/web/components/phoebe_chat_view/tool_calls/surface_organization_setting_tool_ui';
import {
    ToolApprovalCard,
    ToolApprovalQueueCard,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_approval_cards';
import {
    getToolIcon,
    isDomainRenderedToolOutput,
    isSilentInternalToolCall,
    shouldShowToolInputs as shouldShowToolInputsForTool,
    type ToolStepLabel,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_renderers';
import {
    formatToolExecutionDuration,
    GenericToolResultCard,
    getToolExecutionShellStatus,
    type ToolCardPresentation,
    ToolExecutionCardShell,
    ToolIssueCard,
    ToolValueSection,
    useRedactedText,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import {
    isPendingApprovalToolExecutionEntry,
    isSurfaceSettingToolEntry,
    isTeachPhoebeUserQuestion,
    isUserQuestionToolEntry,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_entry_predicates';
import { formatToolName } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_labels';
import {
    EXECUTION_PLAN_TOOL_NAME,
    QUERY_TOOL_NAME,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_name_constants';
import {
    buildStreamingSubagentDraftSummary,
    formatToolOutput,
    renderMonitoringToolLabel,
    renderToolLabel,
    type ToolSummary,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_output_format';
import {
    getProjectedToolStep,
    shouldHideToolNodeForDeclaredPlan,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_step_labels';
import { adaptTranscriptForLegacy } from '@apps/web/components/phoebe_chat_view/transcript/adapt_transcript';
import {
    type AssistantMessageActionRenderer,
    type MessageArtifactRenderer,
    type RenderTimelineEntry,
    type ThoughtEntry,
    type ToolApprovalDecision,
    type ToolApprovalDecisionTarget,
    type ToolApprovalScope,
    type ToolArtifactRenderer,
    type ToolEntryInspectorHandler,
    type ToolExecutionEntry,
    type ToolOutputRenderer,
    type ToolOutputRendererPayload,
    type TranscriptEntry,
} from '@apps/web/components/phoebe_chat_view/transcript_types';
import { useLatchedAnimationEligibility } from '@apps/web/components/phoebe_chat_view/use_latched_animation_eligibility';
import { UserQuestionCard } from '@apps/web/components/phoebe_chat_view/user_questions/user_question_card';
import { Badge } from '@apps/web/components/ui/badge';
import { Markdown } from '@apps/web/components/ui/markdown';
import { Spinner } from '@apps/web/components/ui/spinner';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { type OrganizationEvent } from '@apps/web/hooks/use_organization_events';
import { usePhoebeAgentRunEventStream } from '@apps/web/hooks/use_phoebe_agent_run_event_stream';
import { useThrottledCallback } from '@apps/web/hooks/use_throttled_callback';
import { useTypewriterText } from '@apps/web/hooks/use_typewriter_text';
import { formatDateAndTime } from '@apps/web/lib/date_utils';
import { formatDuration } from '@apps/web/lib/format_utils';
import { cn } from '@apps/web/lib/utils';
import {
    useJoinedOutreach,
    useOutreachAnalysisRunByOutreach,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

function DynamicWidgetClassicFallback({
    summary,
    output,
}: {
    readonly summary: ToolSummary | null;
    readonly output: unknown;
}) {
    if (summary === null) {
        return (
            <GenericToolResultCard
                value={output}
                emptyText="No result"
                presentation="default"
            />
        );
    }
    return (
        <div className="space-y-2.5">
            {summary.items.map((item, index) =>
                item.label === '' ? (
                    <div
                        key={`${item.label}-${index}`}
                        className="text-foreground text-xs"
                    >
                        {item.value}
                    </div>
                ) : (
                    <div key={`${item.label}-${index}`} className="flex gap-3">
                        <span className="text-muted-foreground min-w-[4.5rem] flex-shrink-0 text-xs font-medium">
                            {item.label}
                        </span>
                        {typeof item.value === 'string' ? (
                            <span className="text-foreground flex-1 text-xs">
                                {item.value}
                            </span>
                        ) : (
                            <div className="text-foreground flex-1 text-xs">
                                {item.value}
                            </div>
                        )}
                    </div>
                ),
            )}
        </div>
    );
}

const OUTREACH_EVENT_REFRESH_THROTTLE_MS = 5_000;

function LiveRunReasoningNode({
    node,
    content,
    title,
    isExpanded,
    hasNextNode,
    animateContent,
    onTypewriterProgress,
    connectorClassName,
    dotClassName,
    onToggle,
    hideTitle = false,
}: {
    readonly node: StreamRunNode;
    readonly content: string;
    readonly title: string;
    readonly isExpanded: boolean;
    readonly hasNextNode: boolean;
    readonly animateContent: boolean;
    readonly onTypewriterProgress?: () => void;
    readonly connectorClassName: string;
    readonly dotClassName: string;
    readonly onToggle: () => void;
    // When true, drop the inline "Thought ⌄" title row and always render
    // the reasoning content next to the bullet. Used in the general-agent
    // tree where the outer "Worked for Xs ⌄" wrapper already carries the
    // expand title, so the inner one is redundant.
    readonly hideTitle?: boolean;
}) {
    const showContent = hideTitle
        ? content.length > 0
        : isExpanded && content.length > 0;

    return (
        <div className="relative py-0.5">
            {hasNextNode ? <span className={connectorClassName} /> : null}
            <span className={dotClassName} />
            {hideTitle ? null : (
                <button
                    type="button"
                    onClick={onToggle}
                    className="text-muted-foreground hover:text-foreground flex cursor-pointer items-center gap-1 text-xs transition-colors"
                >
                    <span className="font-semibold">{title}</span>
                    {isExpanded ? (
                        <ChevronDown className="h-3 w-3" />
                    ) : (
                        <ChevronRight className="h-3 w-3" />
                    )}
                </button>
            )}
            {showContent ? (
                <div className={hideTitle ? '' : 'mt-1.5 border-l pl-2'}>
                    <LiveRunReasoningContent
                        node={node}
                        content={content}
                        animateContent={animateContent}
                        onTypewriterProgress={onTypewriterProgress}
                    />
                </div>
            ) : null}
        </div>
    );
}

function LiveRunReasoningContent({
    node,
    content,
    animateContent,
    onTypewriterProgress,
}: {
    readonly node: StreamRunNode;
    readonly content: string;
    readonly animateContent: boolean;
    readonly onTypewriterProgress?: () => void;
}): ReactElement {
    const shouldAnimateContent = useLatchedAnimationEligibility(animateContent);
    const renderedContent = useTypewriterText(content, {
        animate: shouldAnimateContent,
        animationKey: node.id,
        onRevealFrame: shouldAnimateContent ? onTypewriterProgress : undefined,
    });
    const refMap = useMemo(
        () => new Map((node.references ?? []).map((r) => [r.id, r])),
        [node.references],
    );
    const processedContent = processContentForMarkdown(
        renderedContent,
        refMap,
        EMPTY_SLACK_MENTION_MAP,
    );

    return (
        <Markdown
            className="text-muted-foreground text-xs [overflow-wrap:anywhere] break-words"
            components={{
                p: ({ node: _node, ...props }) => (
                    <p className="mb-2 last:mb-0" {...props} />
                ),
                li: ({ node: _node, ...props }) => (
                    <li
                        className="text-xs leading-relaxed break-words"
                        {...props}
                    />
                ),
                a: ({ href, children }) =>
                    renderReferenceAwareAnchor(href, children, refMap),
            }}
        >
            {processedContent}
        </Markdown>
    );
}

function LiveRunResponseNode({
    node,
    content,
    hasNextNode,
    animateContent,
    onTypewriterProgress,
    connectorClassName,
    dotClassName,
}: {
    readonly node: StreamRunNode;
    readonly content: string;
    readonly hasNextNode: boolean;
    readonly animateContent: boolean;
    readonly onTypewriterProgress?: () => void;
    readonly connectorClassName: string;
    readonly dotClassName: string;
}) {
    const shouldAnimateContent = useLatchedAnimationEligibility(animateContent);
    const renderedContent = useTypewriterText(content, {
        animate: shouldAnimateContent,
        animationKey: node.id,
        onRevealFrame: shouldAnimateContent ? onTypewriterProgress : undefined,
    });
    const refMap = useMemo(
        () => new Map((node.references ?? []).map((r) => [r.id, r])),
        [node.references],
    );
    const processedContent = processContentForMarkdown(
        renderedContent,
        refMap,
        EMPTY_SLACK_MENTION_MAP,
    );

    return (
        <div className="relative py-0.5">
            {hasNextNode ? <span className={connectorClassName} /> : null}
            <span className={dotClassName} />
            <Markdown
                className="text-muted-foreground text-xs [overflow-wrap:anywhere] break-words"
                components={{
                    p: ({ node: _node, ...props }) => (
                        <p className="mb-2 last:mb-0" {...props} />
                    ),
                    li: ({ node: _node, ...props }) => (
                        <li
                            className="text-xs leading-relaxed break-words"
                            {...props}
                        />
                    ),
                    a: ({ href, children }) =>
                        renderReferenceAwareAnchor(href, children, refMap),
                }}
            >
                {processedContent}
            </Markdown>
        </div>
    );
}

// Project the LATEST `update_execution_plan` tool call onto the
// execution-plan card. The agent owns the statuses — each call
// replaces the whole list. Returns null when the agent hasn't
// published a plan yet (caller falls back to the tool-grouping
// behavior).
//
// When the run finished cleanly, or the final response has started, any
// trailing `pending` / `in_progress` items (most commonly the
// `Compose response` item — the agent can't mark it done because the
// turn ends with `assistant_text`) are promoted to `completed`.
function findDeclaredExecutionPlan(
    renderNodes: ReadonlyArray<StreamRunNode>,
    finalizePlanItems: boolean,
): {
    readonly latestPlan: ExecutionPlanData | null;
} | null {
    let firstPlanNode: StreamRunNode | null = null;
    let latestPlanNode: StreamRunNode | null = null;
    for (let i = 0; i < renderNodes.length; i += 1) {
        const node = renderNodes[i];
        if (
            node &&
            node.kind === 'tool' &&
            node.toolName === EXECUTION_PLAN_TOOL_NAME
        ) {
            if (firstPlanNode === null) {
                firstPlanNode = node;
            }
            latestPlanNode = node;
        }
    }
    if (firstPlanNode === null || latestPlanNode === null) {
        return null;
    }
    const args = latestPlanNode.arguments;
    if (args === null || typeof args !== 'object' || Array.isArray(args)) {
        return null;
    }
    const rawItems = (args as Record<string, unknown>)['items'];
    if (!Array.isArray(rawItems)) {
        return null;
    }
    const items = normalizeDeclaredExecutionPlanItems(
        rawItems,
        (_item, idx) => `plan-${firstPlanNode.id}-${idx}`,
    );
    if (items.length === 0) {
        return null;
    }
    // Run completed cleanly — flip any leftover pending/in_progress
    // items to completed. The `Compose response` item never gets
    // marked done by the agent itself because the turn ends after
    // the assistant_text reply, so the model has no opportunity to
    // send one more update_execution_plan call.
    // Note saves are deliberately absent here: the "Phoebe took a note"
    // chip in the transcript is the note's only surface, so the working
    // stream must not narrate it a second time.
    const finalItems: Array<ExecutionPlanItem> = finalizePlanItems
        ? items.map((item) => ({
              ...item,
              status: 'completed' as const,
          }))
        : items;
    return {
        latestPlan: { items: finalItems },
    };
}

function toolExecutionEntryFromStreamNode(
    node: StreamRunNode,
    runId: string,
): ToolExecutionEntry {
    return {
        kind: 'tool-execution',
        id: node.id,
        runId,
        toolCallEventId: node.toolCallEventId ?? null,
        createdAt: node.createdAt,
        name: node.toolName ?? 'tool_call',
        callId: node.toolCallId,
        status: node.status,
        arguments: node.arguments,
        output: node.result,
        text: typeof node.result === 'string' ? node.result : null,
        error: node.error,
        lifecycleStatus: node.isComplete
            ? node.error
                ? 'failed'
                : 'completed'
            : 'running',
        isComplete: node.isComplete,
        durationMs: node.durationMs ?? null,
    };
}

function getInferredInterstitialResponseIds(
    nodes: ReadonlyArray<StreamRunNode>,
): ReadonlySet<string> {
    const result = new Set<string>();
    let hasLaterTool = false;
    for (let index = nodes.length - 1; index >= 0; index -= 1) {
        const node = nodes[index];
        if (!node) {
            continue;
        }
        if (node.kind === 'tool') {
            hasLaterTool = true;
            continue;
        }
        if (node.kind === 'response' && hasLaterTool) {
            result.add(node.id);
        }
    }
    return result;
}

export function LiveRunTree({
    run,
    showDurationLabels = true,
    hideLastResponseNode = true,
    animatedReasoningNodeId = null,
    animatedResponseNodeId = null,
    onTypewriterProgress,
    onCaregiverClick,
    onClientClick,
    showGeneralOutreachProgressCards = false,
    hideTraceTitles = false,
    subagentDraftProgress = null,
    navigateOnDraftedOutreach = false,
}: {
    run: StreamRunState;
    showDurationLabels?: boolean;
    hideLastResponseNode?: boolean;
    animatedReasoningNodeId?: string | null;
    animatedResponseNodeId?: string | null;
    onTypewriterProgress?: () => void;
    onCaregiverClick?: (caregiverId: string) => void;
    onClientClick?: (clientId: string) => void;
    showGeneralOutreachProgressCards?: boolean;
    hideTraceTitles?: boolean;
    subagentDraftProgress?: SubagentDraftProgress | null;
    navigateOnDraftedOutreach?: boolean;
}) {
    const redactText = useRedactedText();
    const dynamicWidgetBuildContext = useDynamicWidgetBuildContext();
    const promptAction = useDynamicWidgetPromptAction();
    const getActionState = useDynamicWidgetActionStateResolver();
    const [expandedThoughtIds, setExpandedThoughtIds] = useState<
        Record<string, boolean>
    >({});
    const [expandedToolIds, setExpandedToolIds] = useState<
        Record<string, boolean>
    >({});
    const orderedNodes = useMemo(
        () =>
            run.orderedNodeIds
                .map((nodeId) => run.nodesById[nodeId])
                .filter((node): node is StreamRunNode => node !== undefined)
                .filter((node) => isLiveRunTreeDisplayNode(node, run.status)),
        [run.nodesById, run.orderedNodeIds, run.status],
    );
    const nodesForDisplay = useMemo<Array<StreamRunNode>>(() => {
        if (run.status === 'running' || !hideLastResponseNode) {
            return orderedNodes;
        }

        let lastResponseNodeId: string | null = null;
        for (let index = orderedNodes.length - 1; index >= 0; index -= 1) {
            const node = orderedNodes[index];
            if (node?.kind === 'response') {
                lastResponseNodeId = node.id;
                break;
            }
        }

        if (!lastResponseNodeId) {
            return orderedNodes;
        }

        return orderedNodes.filter((node) => node.id !== lastResponseNodeId);
    }, [hideLastResponseNode, orderedNodes, run.status]);
    const renderNodes = useMemo(
        () =>
            nodesForDisplay.filter((node) =>
                isLiveRunTreeRenderableNode(node, run.status),
            ),
        [nodesForDisplay, run.status],
    );
    // Mirror the UI's create-outreach flow: when the coordinator approves
    // a draft and it completes in this live run, navigate to the outreach
    // details page (where the recommendation analysis surfaces). Hydrating
    // an already-finished run never navigates (see completedLive below).
    const navigate = useNavigate();
    const pendingDraftNodeIdsRef = useRef<Set<string>>(new Set());
    useEffect(() => {
        // With dynamic widgets on, the T2 progress card renders the drafted
        // outreach inline — auto-navigating away would abandon the card
        // mid-flow (the card links to the outreach page instead).
        if (!navigateOnDraftedOutreach || dynamicWidgetBuildContext !== null) {
            return;
        }
        for (const node of renderNodes) {
            if (node.kind !== 'tool') {
                continue;
            }
            if (node.toolName !== 'draft_agent_outreach') {
                continue;
            }
            if (!node.isComplete) {
                pendingDraftNodeIdsRef.current.add(node.id);
                continue;
            }
            const outreachId = draftOutreachIdFromToolOutput(
                node.toolName,
                node.result,
            );
            // Live completion = we watched the node go pending→complete, or
            // the run is still streaming (an approval resume re-delivers the
            // node already complete). Reopened historical runs are neither.
            const completedLive =
                pendingDraftNodeIdsRef.current.has(node.id) ||
                run.status === 'running';
            if (
                outreachId === null ||
                !completedLive ||
                autoNavigatedDraftOutreachIds.has(outreachId)
            ) {
                continue;
            }
            autoNavigatedDraftOutreachIds.add(outreachId);
            // Already viewing this outreach's page — nothing to do.
            if (window.location.pathname.includes(outreachId)) {
                continue;
            }
            void navigate({
                to: '/outreaches/$callout-id',
                params: { 'callout-id': outreachId },
                search: { openChat: true },
            });
        }
    }, [
        dynamicWidgetBuildContext,
        navigate,
        navigateOnDraftedOutreach,
        renderNodes,
        run.status,
    ]);
    const responseStarted = useMemo(
        () =>
            run.orderedNodeIds.some((nodeId) => {
                const node = run.nodesById[nodeId];
                return (
                    node?.kind === 'response' && node.content.trim().length > 0
                );
            }),
        [run.nodesById, run.orderedNodeIds],
    );
    const hasPendingUserInputTool = useMemo(
        () =>
            run.orderedNodeIds.some((nodeId) => {
                const node = run.nodesById[nodeId];
                return (
                    node?.kind === 'tool' &&
                    node.status === 'pending_user_input' &&
                    !node.isComplete
                );
            }),
        [run.nodesById, run.orderedNodeIds],
    );
    // The server does not tag streamed assistant_text between tool calls as
    // interstitial, so infer it from the later tool in the same run.
    const inferredInterstitialResponseIds = useMemo(
        () => getInferredInterstitialResponseIds(renderNodes),
        [renderNodes],
    );
    const showProjectedMilestones = showGeneralOutreachProgressCards;
    // Only finalize plan items when a non-interstitial (i.e. final)
    // response has started. Interstitial narrations between tool calls
    // should NOT promote in-progress items to completed. Iterate
    // renderNodes (not run.orderedNodeIds) to match the same set that
    // inferredInterstitialResponseIds is built from.
    const finalResponseStarted = useMemo(
        () =>
            renderNodes.some(
                (node) =>
                    node.kind === 'response' &&
                    node.content.trim().length > 0 &&
                    node.responseKind !== 'interstitial' &&
                    !inferredInterstitialResponseIds.has(node.id),
            ),
        [renderNodes, inferredInterstitialResponseIds],
    );
    // Project the execution plan from the run's actual tool stream.
    // Walk tool nodes in order, group consecutive same-tool-name calls
    // into one card item, derive status from tool_call vs tool_output.
    const executionPlanRender = useMemo<ExecutionPlanRender>(() => {
        // Historical runs only: the most recent `update_execution_plan`
        // call, whose statuses the agent owned. The tool was removed from
        // every runtime, so this is null for every new run.
        const declaredPlan = findDeclaredExecutionPlan(
            renderNodes,
            run.status === 'completed' || finalResponseStarted,
        );
        if (declaredPlan !== null) {
            return {
                ...declaredPlan,
                source: 'declared',
            };
        }
        if (!showProjectedMilestones) {
            return {
                latestPlan: null,
                source: null,
            };
        }
        // General-agent views without a declared plan fall back to grouping
        // the actual tool stream into coarse progress rows.
        type WipItem = {
            readonly id: string;
            readonly labels: ToolStepLabel;
        };
        const wipItems: Array<WipItem> = [];
        const toolsByItemId = new Map<string, Array<StreamRunNode>>();
        let currentItemId: string | null = null;
        let currentItemGroupKey: string | null = null;
        for (let i = 0; i < renderNodes.length; i += 1) {
            const node = renderNodes[i];
            if (!node || node.kind !== 'tool') {
                continue;
            }
            if (isSilentInternalToolCall(node.toolName, node.arguments)) {
                continue;
            }
            const projectedStep = getProjectedToolStep({
                toolName: node.toolName,
                toolArguments: node.arguments,
            });
            if (projectedStep === null) {
                continue;
            }
            if (projectedStep.groupKey !== currentItemGroupKey) {
                currentItemId = node.id;
                currentItemGroupKey = projectedStep.groupKey;
                wipItems.push({
                    id: currentItemId,
                    labels: projectedStep.labels,
                });
                toolsByItemId.set(currentItemId, []);
            }
            const bucket = currentItemId
                ? (toolsByItemId.get(currentItemId) ?? [])
                : [];
            bucket.push(node);
            if (currentItemId) {
                toolsByItemId.set(currentItemId, bucket);
            }
        }
        const finalItems: Array<ExecutionPlanItem> = wipItems.map((item) => {
            const tools = toolsByItemId.get(item.id) ?? [];
            const toolsDone =
                tools.length > 0 &&
                tools.every((toolNode) => toolNode.isComplete);
            // Only mark completed when the underlying tools actually
            // finished. If the run ended while tools were still in
            // flight (run error / interruption), leave status as
            // in_progress so the checklist adapter can render it as
            // actively running, errored, or cleanly completed based on
            // the final run status.
            return {
                id: item.id,
                title: toolsDone ? item.labels.complete : item.labels.active,
                status: toolsDone ? 'completed' : 'in_progress',
                receipt: toolsDone ? getProjectedMilestoneReceipt(tools) : null,
                durationLabel: toolsDone
                    ? getProjectedMilestoneDurationLabel(tools)
                    : null,
            };
        });
        // Append a synthetic "Planning next steps" item only while the
        // run is actively running and the LLM is between tool completion
        // and the response stream. Once the response starts OR the run
        // ends, drop the item entirely — leaving a perpetually-spinning
        // "Planning next steps" card after the answer has streamed is
        // worse than showing nothing.
        const allObservedItemsCompleted =
            finalItems.length > 0 &&
            finalItems.every((i) => i.status === 'completed');
        if (
            run.status === 'running' &&
            allObservedItemsCompleted &&
            !responseStarted &&
            !hasPendingUserInputTool
        ) {
            finalItems.push({
                id: 'finalizing',
                title: 'Planning next steps',
                status: 'in_progress',
            });
        }
        const latestPlan: ExecutionPlanData | null =
            finalItems.length > 0 ? { items: finalItems } : null;
        return {
            latestPlan,
            source: latestPlan ? 'projected' : null,
        };
    }, [
        finalResponseStarted,
        hasPendingUserInputTool,
        renderNodes,
        responseStarted,
        run.status,
        showProjectedMilestones,
    ]);

    useEffect(() => {
        setExpandedThoughtIds((current) => {
            const next: Record<string, boolean> = {};
            for (const node of renderNodes) {
                if (node.kind !== 'reasoning') {
                    continue;
                }
                if (current[node.id]) {
                    next[node.id] = true;
                }
            }
            return next;
        });
        setExpandedToolIds((current) => {
            const next: Record<string, boolean> = {};
            for (const node of renderNodes) {
                if (node.kind !== 'tool') {
                    continue;
                }
                if (current[node.id]) {
                    next[node.id] = true;
                }
            }
            return next;
        });
    }, [renderNodes]);

    if (renderNodes.length === 0 && !showProjectedMilestones) {
        return null;
    }

    // Render declared plans or projected outreach milestones at the top of
    // the legacy timeline.
    // Hide the progress card while the shimmer is still in its initial state
    // so the initial moments show just the shimmer; once items land, the card
    // appears alongside the shimmer's next synonym. Skip the empty card on
    // completed runs that produced no labeled tools so we don't leave an
    // orphan progress card in history.
    const cardPlan = executionPlanRender.latestPlan ?? { items: [] };
    const executionPlanSource = executionPlanRender.source;
    const shouldRenderExecutionPlan =
        cardPlan.items.length > 0 &&
        executionPlanSource !== null &&
        (showProjectedMilestones || executionPlanSource === 'declared');
    const executionPlanCard = shouldRenderExecutionPlan ? (
        <div className="-ml-6 py-0.5">
            <ExecutionPlanTraceCard
                plan={cardPlan}
                nested={true}
                runStatus={run.status}
                parseFailed={false}
                source={executionPlanSource}
                defaultExpanded={true}
            />
        </div>
    ) : null;
    return (
        <div className="ml-2 pl-4">
            <div className="space-y-2">
                {executionPlanCard}
                {renderNodes.map((node, index) => {
                    const hasNextNode = index < renderNodes.length - 1;
                    const dotClassName =
                        'bg-border absolute top-[0.375rem] -left-4 h-1.5 w-1.5 -translate-x-1/2 rounded-full';
                    const connectorClassName =
                        'bg-border/80 absolute top-[0.875rem] -left-4 bottom-[-0.75rem] w-px -translate-x-1/2';

                    // Hide tool rows once their friendly checklist is visible.
                    // If a declared plan has no user-facing items, keep the
                    // plan tool row as a plain "Live status" fallback.
                    if (node.kind === 'tool') {
                        const shouldHideDeclaredPlanTool =
                            node.toolName === EXECUTION_PLAN_TOOL_NAME &&
                            executionPlanRender.source === 'declared' &&
                            shouldRenderExecutionPlan;
                        const shouldHideGeneralProgressTool =
                            showGeneralOutreachProgressCards &&
                            node.toolName !== EXECUTION_PLAN_TOOL_NAME;
                        const shouldHideDeclaredInternalTool =
                            executionPlanRender.source === 'declared' &&
                            shouldHideToolNodeForDeclaredPlan(node.toolName);
                        // Loading a skill is Phoebe fetching her own
                        // instructions. It says nothing about the scheduler's
                        // work, so it stays out of the trace whether or not a
                        // plan was declared.
                        const shouldHideSkillContextTool =
                            node.toolName !== null &&
                            SKILL_CONTEXT_TOOL_NAMES.has(node.toolName);
                        if (
                            shouldHideDeclaredPlanTool ||
                            shouldHideGeneralProgressTool ||
                            shouldHideDeclaredInternalTool ||
                            shouldHideSkillContextTool ||
                            isSilentInternalToolCall(
                                node.toolName,
                                node.arguments,
                            )
                        ) {
                            return null;
                        }
                    }

                    if (node.kind === 'reasoning') {
                        const content = node.content.trim();
                        if (showProjectedMilestones && content.length === 0) {
                            return null;
                        }
                        const isActiveThought =
                            run.status === 'running' && !hasNextNode;
                        const isExpanded =
                            expandedThoughtIds[node.id] ?? isActiveThought;
                        const title = isActiveThought
                            ? 'Thinking'
                            : showDurationLabels
                              ? formatThoughtTitle(node.durationMs)
                              : 'Thought';
                        return (
                            <LiveRunReasoningNode
                                key={node.id}
                                node={node}
                                content={content}
                                title={title}
                                isExpanded={isExpanded}
                                hasNextNode={hasNextNode}
                                animateContent={
                                    node.id === animatedReasoningNodeId
                                }
                                onTypewriterProgress={onTypewriterProgress}
                                connectorClassName={connectorClassName}
                                dotClassName={dotClassName}
                                onToggle={() =>
                                    setExpandedThoughtIds((current) => ({
                                        ...current,
                                        [node.id]: !isExpanded,
                                    }))
                                }
                                hideTitle={hideTraceTitles}
                            />
                        );
                    }

                    if (node.kind === 'response') {
                        const content = node.content.trim();
                        if (content.length === 0) {
                            return null;
                        }
                        // General agent: intermediate narration
                        // ("Let me also check X…") is part of the agent's
                        // thinking trace, which the shimmer status indicator
                        // represents above. Suppress the prose so only the
                        // final response (non-interstitial) renders below.
                        if (
                            showProjectedMilestones &&
                            (node.responseKind === 'interstitial' ||
                                inferredInterstitialResponseIds.has(node.id))
                        ) {
                            return null;
                        }
                        return (
                            <LiveRunResponseNode
                                key={node.id}
                                node={node}
                                content={content}
                                hasNextNode={hasNextNode}
                                animateContent={
                                    node.id === animatedResponseNodeId
                                }
                                onTypewriterProgress={onTypewriterProgress}
                                connectorClassName={connectorClassName}
                                dotClassName={dotClassName}
                            />
                        );
                    }

                    const toolName = node.toolName ?? 'tool_call';
                    const ToolIcon = getToolIcon(toolName) ?? Search;
                    const isStreamingSubagentDrafts =
                        toolName === 'get_outreach_suggestion_drafts' &&
                        !node.isComplete &&
                        subagentDraftProgress !== null;
                    const streamingSubagentSummary = isStreamingSubagentDrafts
                        ? buildStreamingSubagentDraftSummary(
                              subagentDraftProgress,
                          )
                        : null;
                    const resultReviewMessage = node.isComplete
                        ? getToolResultReviewMessage(node.result)
                        : null;
                    const reviewMessage = node.error ?? resultReviewMessage;
                    const dynamicWidgetEntry = toolExecutionEntryFromStreamNode(
                        node,
                        run.runId,
                    );
                    const dynamicWidgetModel =
                        dynamicWidgetBuildContext === null
                            ? null
                            : buildDynamicWidget(
                                  dynamicWidgetEntry,
                                  dynamicWidgetBuildContext,
                              );
                    const classicDynamicWidgetSummary =
                        node.isComplete && node.result !== null
                            ? formatToolOutput(
                                  toolName,
                                  node.result,
                                  onCaregiverClick,
                                  onClientClick,
                                  node.arguments,
                              )
                            : null;
                    const dynamicWidgetContent =
                        dynamicWidgetModel === null
                            ? null
                            : renderDynamicWidget(
                                  dynamicWidgetModel,
                                  {
                                      onCaregiverClick,
                                      onClientClick,
                                      ...(promptAction !== null
                                          ? {
                                                onWidgetAction: promptAction,
                                                onPromptAction: promptAction,
                                            }
                                          : {}),
                                      ...(getActionState !== null
                                          ? { getActionState }
                                          : {}),
                                  },
                                  <DynamicWidgetClassicFallback
                                      summary={classicDynamicWidgetSummary}
                                      output={node.result}
                                  />,
                              );
                    const summary =
                        dynamicWidgetContent !== null
                            ? {
                                  items: [
                                      {
                                          label: '',
                                          value: dynamicWidgetContent,
                                      },
                                  ],
                              }
                            : (classicDynamicWidgetSummary ??
                              streamingSubagentSummary);
                    // Errored or denied calls must not claim success — keep
                    // the active-form label; the error styling explains why.
                    const labelIsComplete =
                        node.isComplete && reviewMessage === null;
                    const baseLabel =
                        toolName === 'monitor_outreach'
                            ? renderMonitoringToolLabel({
                                  isComplete: labelIsComplete,
                              })
                            : formatToolName(
                                  toolName,
                                  labelIsComplete,
                                  node.arguments,
                                  labelIsComplete ? node.result : undefined,
                              );
                    const label =
                        toolName === 'monitor_outreach'
                            ? baseLabel
                            : renderToolLabel(
                                  baseLabel,
                                  node.arguments,
                                  onCaregiverClick,
                                  labelIsComplete ? node.result : undefined,
                              );
                    const isPlainToolLabel =
                        typeof label === 'string' || typeof label === 'number';
                    const isExpanded =
                        isStreamingSubagentDrafts ||
                        (expandedToolIds[node.id] ?? false);
                    const toggleToolNode = () =>
                        setExpandedToolIds((current) => ({
                            ...current,
                            [node.id]: !isExpanded,
                        }));
                    const toolNodeIcon = !node.isComplete ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <ToolIcon
                            className={
                                ToolIcon === Search ? 'h-3 w-3' : 'h-3.5 w-3.5'
                            }
                        />
                    );
                    const toolNodeChevron = isExpanded ? (
                        <ChevronDown className="h-3 w-3" />
                    ) : (
                        <ChevronRight className="h-3 w-3" />
                    );
                    const textResult =
                        typeof node.result === 'string' &&
                        !isDomainRenderedToolOutput(toolName)
                            ? redactText(node.result)
                            : null;
                    const outputReviewMessage = node.error
                        ? null
                        : resultReviewMessage;
                    const shouldShowOutputReviewCard =
                        outputReviewMessage !== null && summary === null;
                    // A thought says what Phoebe did; the raw call and its
                    // return are machinery. Dumping the argument and result
                    // objects here spent most of the thought's height on field
                    // names nobody reads — the summary line above each entry
                    // already carries what happened.
                    const shouldShowGenericResult = false;
                    const shouldShowToolInputs = false;
                    if (
                        (node.status === 'pending_approval' ||
                            node.status === 'pending_user_input') &&
                        !node.isComplete &&
                        node.toolCallId
                    ) {
                        return null;
                    }
                    const generalOutreachProgressSeed =
                        showGeneralOutreachProgressCards
                            ? getGeneralOutreachProgressSeedFromTool({
                                  toolName,
                                  isComplete: node.isComplete,
                                  output: node.result,
                              })
                            : null;

                    if (generalOutreachProgressSeed) {
                        return (
                            <div key={node.id} className="relative py-0.5">
                                {hasNextNode ? (
                                    <span className={connectorClassName} />
                                ) : null}
                                <span className={dotClassName} />
                                <GeneralOutreachProgressCard
                                    seed={generalOutreachProgressSeed}
                                    nested={true}
                                    onCaregiverClick={onCaregiverClick}
                                    error={node.error}
                                />
                            </div>
                        );
                    }

                    if (toolName === QUERY_TOOL_NAME) {
                        return (
                            <div key={node.id} className="relative py-0.5">
                                {hasNextNode ? (
                                    <span className={connectorClassName} />
                                ) : null}
                                <span className={dotClassName} />
                                <QueryToolRow
                                    label={formatToolName(
                                        toolName,
                                        labelIsComplete,
                                        node.arguments,
                                    )}
                                    icon={toolNodeIcon}
                                />
                                {node.error ? (
                                    <div className="mt-1.5 border-l pl-2">
                                        <ToolIssueCard
                                            title="Tool step needs review"
                                            error={node.error}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        );
                    }

                    return (
                        <div key={node.id} className="relative py-0.5">
                            {dynamicWidgetBuildContext !== null &&
                            dynamicWidgetModel === null ? (
                                <DynamicWidgetFallbackMetric
                                    entry={dynamicWidgetEntry}
                                />
                            ) : null}
                            {hasNextNode ? (
                                <span className={connectorClassName} />
                            ) : null}
                            <span className={dotClassName} />
                            {isPlainToolLabel ? (
                                <button
                                    type="button"
                                    aria-expanded={isExpanded}
                                    onClick={toggleToolNode}
                                    className="text-muted-foreground hover:text-foreground flex cursor-pointer items-center gap-2 text-xs transition-colors"
                                >
                                    {toolNodeIcon}
                                    <span className="font-medium">{label}</span>
                                    {toolNodeChevron}
                                </button>
                            ) : (
                                <div className="text-muted-foreground flex items-center gap-2 text-xs">
                                    {toolNodeIcon}
                                    <span className="font-medium">{label}</span>
                                    <button
                                        type="button"
                                        aria-expanded={isExpanded}
                                        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${baseLabel}`}
                                        onClick={toggleToolNode}
                                        className="hover:text-foreground cursor-pointer transition-colors"
                                    >
                                        {toolNodeChevron}
                                    </button>
                                </div>
                            )}
                            {isExpanded ? (
                                <div className="mt-1.5 border-l pl-2">
                                    {node.error ? (
                                        <ToolIssueCard
                                            title="Tool step needs review"
                                            error={node.error}
                                        />
                                    ) : null}
                                    {shouldShowOutputReviewCard ? (
                                        <ToolIssueCard
                                            title="This step needs review"
                                            error={outputReviewMessage}
                                        />
                                    ) : null}
                                    {textResult ? (
                                        <Markdown className="text-muted-foreground text-xs">
                                            {textResult}
                                        </Markdown>
                                    ) : null}
                                    {summary ? (
                                        <div className="mb-2 space-y-1.5">
                                            {summary.items.map(
                                                (item, itemIndex) => {
                                                    if (item.label === '') {
                                                        return (
                                                            <div
                                                                key={`${node.id}-summary-${itemIndex}`}
                                                                className="text-foreground text-xs"
                                                            >
                                                                {item.value}
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <div
                                                            key={`${node.id}-summary-${item.label}-${itemIndex}`}
                                                            className="flex gap-2"
                                                        >
                                                            <span className="text-muted-foreground min-w-[4rem] text-xs font-medium">
                                                                {item.label}
                                                            </span>
                                                            {typeof item.value ===
                                                            'string' ? (
                                                                <span className="text-foreground text-xs">
                                                                    {item.value}
                                                                </span>
                                                            ) : (
                                                                <div className="text-foreground text-xs">
                                                                    {item.value}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                },
                                            )}
                                        </div>
                                    ) : null}
                                    {shouldShowGenericResult ? (
                                        <ToolValueSection
                                            title="Result"
                                            value={node.result}
                                            emptyText="No result"
                                        />
                                    ) : null}
                                    {shouldShowToolInputs ? (
                                        <ToolValueSection
                                            title="Inputs"
                                            value={node.arguments}
                                            emptyText="No inputs"
                                        />
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// Auto-navigation must fire at most once per created outreach for the
// whole page load: a per-mount ref resets when back-navigation remounts
// the chat and would shove the user forward again.
const autoNavigatedDraftOutreachIds = new Set<string>();

export function resetDraftOutreachAutoNavigationForTest() {
    autoNavigatedDraftOutreachIds.clear();
}

// The draft result carries the new outreach id for the auto-navigation effect.
function draftOutreachIdFromToolOutput(
    toolName: string | null,
    output: unknown,
): string | null {
    if (toolName !== 'draft_agent_outreach' || !isRecord(output)) {
        return null;
    }
    const outreachId = output['outreach_id'];
    return typeof outreachId === 'string' ? outreachId : null;
}

export function shouldUseCompletedToolExecutionLabel(
    entry: ToolExecutionEntry,
    visuallyComplete: boolean = entry.isComplete,
): boolean {
    return (
        visuallyComplete &&
        getToolExecutionReviewMessage(entry) === null &&
        entry.approvalState !== 'denied'
    );
}

export type GeneralOutreachProgressSeed = {
    readonly outreachId: string;
    readonly source: 'draft' | 'details';
    readonly data: Record<string, unknown>;
};

export function getGeneralOutreachProgressSeedFromTool({
    toolName,
    isComplete,
    output,
}: {
    readonly toolName: string;
    readonly isComplete: boolean;
    readonly output: unknown;
}): GeneralOutreachProgressSeed | null {
    if (
        toolName !== 'draft_agent_outreach' &&
        toolName !== 'callouts_details'
    ) {
        return null;
    }
    if (!isComplete || !isRecord(output)) {
        return null;
    }

    const outreachId =
        getStringValue(output, 'outreach_id') ??
        getStringValue(output, 'callout_id') ??
        getStringValue(output, 'id');
    if (!outreachId) {
        return null;
    }

    return {
        outreachId,
        source: toolName === 'draft_agent_outreach' ? 'draft' : 'details',
        data: output,
    };
}

function getGeneralOutreachProgressSeed(
    entry: ToolExecutionEntry,
): GeneralOutreachProgressSeed | null {
    return getGeneralOutreachProgressSeedFromTool({
        toolName: entry.name,
        isComplete: entry.isComplete,
        output: entry.output,
    });
}

function mergeSuggestionRows(
    seedData: Record<string, unknown>,
    liveData: Record<string, unknown> | null,
): Array<Record<string, unknown>> {
    const seedSuggestions = Array.isArray(seedData['suggested_caregivers'])
        ? seedData['suggested_caregivers'].filter(isRecord)
        : [];
    const liveHasSuggestions = Array.isArray(
        liveData?.['suggested_caregivers'],
    );
    if (!liveHasSuggestions) {
        return seedSuggestions;
    }
    const liveSuggestions =
        liveData && Array.isArray(liveData['suggested_caregivers'])
            ? liveData['suggested_caregivers'].filter(isRecord)
            : [];

    const seedByCaregiverId = new Map<string, Record<string, unknown>>();
    seedSuggestions.forEach((suggestion) => {
        const caregiverId = getStringValue(suggestion, 'caregiver_id');
        if (caregiverId) {
            seedByCaregiverId.set(caregiverId, suggestion);
        }
    });

    return liveSuggestions.map((suggestion) => {
        const caregiverId = getStringValue(suggestion, 'caregiver_id');
        const seedSuggestion = caregiverId
            ? seedByCaregiverId.get(caregiverId)
            : null;
        return seedSuggestion
            ? { ...seedSuggestion, ...suggestion }
            : suggestion;
    });
}

function getGeneralOutreachShifts(
    seedData: Record<string, unknown>,
    liveData: Record<string, unknown> | null,
): Array<Record<string, unknown>> {
    if (liveData && Array.isArray(liveData['shifts'])) {
        return liveData['shifts'].filter(isRecord);
    }
    if (Array.isArray(seedData['shifts'])) {
        return seedData['shifts'].filter(isRecord);
    }
    const shift = isRecord(seedData['shift']) ? seedData['shift'] : null;
    return shift ? [shift] : [];
}

function formatGeneralOutreachShift(
    shift: Record<string, unknown>,
    timezone: string,
): string {
    const patientName =
        getStringValue(shift, 'patient_name') ??
        getStringValue(shift, 'client_name') ??
        'Shift';
    const shiftDay = getStringValue(shift, 'shift_day');
    const startTime = getStringValue(shift, 'start_time');
    const endTime = getStringValue(shift, 'end_time');
    if (shiftDay && startTime && endTime) {
        return `${patientName} • ${shiftDay}, ${startTime}-${endTime}`;
    }
    if (shiftDay && startTime) {
        return `${patientName} • ${shiftDay}, ${startTime}`;
    }

    const shiftStart = getStringValue(shift, 'shift_start_time');
    if (shiftStart) {
        return `${patientName} • ${formatDateAndTime(shiftStart, timezone)}`;
    }

    return patientName;
}

function formatGeneralOutreachStatus(value: string | null): string | null {
    if (!value) {
        return null;
    }
    return value
        .split(/[_\s-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function getResponseCountParts(
    seedData: Record<string, unknown>,
    liveData: Record<string, unknown> | null,
): Array<string> {
    const responseCounts = liveData?.['response_counts'];
    if (isRecord(responseCounts)) {
        const accepted = getNumberValue(responseCounts, 'accepted_count') ?? 0;
        const declined = getNumberValue(responseCounts, 'declined_count') ?? 0;
        const notAFit = getNumberValue(responseCounts, 'not_a_fit_count') ?? 0;
        const pending = getNumberValue(responseCounts, 'pending_count') ?? 0;
        const needsReview =
            getNumberValue(responseCounts, 'needs_review_count') ?? 0;
        const contacted =
            getNumberValue(responseCounts, 'contacted_count') ??
            accepted + declined + pending + needsReview;
        const parts = [
            contacted > 0 ? `${contacted} contacted` : null,
            accepted > 0 ? `${accepted} accepted` : null,
            declined > 0 ? `${declined} declined` : null,
            notAFit > 0 ? `${notAFit} not fit` : null,
            needsReview > 0 ? `${needsReview} need review` : null,
        ].filter((part): part is string => part !== null);
        if (parts.length > 0) {
            return parts;
        }
    }

    const contactAttemptSummary = isRecord(seedData['contact_attempt_summary'])
        ? seedData['contact_attempt_summary']
        : null;
    const attemptCount = getNumberValue(contactAttemptSummary, 'count') ?? 0;
    if (attemptCount === 0) {
        return [];
    }

    const caregiverResponses = isRecord(
        contactAttemptSummary?.['by_caregiver_response'],
    )
        ? contactAttemptSummary?.['by_caregiver_response']
        : {};
    const interested =
        getNumberValue(caregiverResponses, 'Available and interested') ??
        getNumberValue(caregiverResponses, 'available_interested') ??
        0;
    return [
        `${attemptCount} contacted`,
        interested > 0 ? `${interested} interested` : null,
    ].filter((part): part is string => part !== null);
}

export function GeneralOutreachProgressCard({
    seed,
    nested,
    onCaregiverClick,
    error,
}: {
    readonly seed: GeneralOutreachProgressSeed;
    readonly nested: boolean;
    readonly onCaregiverClick?: (caregiverId: string) => void;
    readonly error?: string | null;
}) {
    const { organizationId, timezone } = usePhoebeChatOrganizationScope();
    const { data: outreachAgentRun, mutate: mutateOutreachAgentRun } =
        useOutreachAnalysisRunByOutreach(organizationId, seed.outreachId);
    const outreachAgentRunId = outreachAgentRun?.id ?? null;
    const outreachAgentRunStream = usePhoebeAgentRunEventStream({
        organizationId,
        phoebeAgentRunId: outreachAgentRunId,
        enabled: outreachAgentRunId !== null,
    });
    const outreachAgentRunState = usePhoebeAgentEventFeedChatViewState({
        events: outreachAgentRunStream.events,
        pendingEvents: outreachAgentRunStream.pendingEvents,
        fallbackRunStatus: outreachAgentRun?.status ?? null,
        phoebeAgentRunId: outreachAgentRunId,
    });
    const { data: liveOutreach, mutate: mutateOutreach } = useJoinedOutreach(
        organizationId,
        seed.outreachId,
        { reason: 'general-agent-outreach-progress-card' },
    );
    const liveData = isRecord(liveOutreach) ? liveOutreach : null;
    const liveAgentStatus = getStringValue(liveData, 'agent_status');
    const suggestions = mergeSuggestionRows(seed.data, liveData);
    const recommendationStatus =
        suggestions.length > 0
            ? 'ready'
            : liveData
              ? (getStringValue(liveData, 'recommendation_status') ??
                liveAgentStatus)
              : getStringValue(seed.data, 'recommendation_status');
    const isFindingCaregivers =
        suggestions.length === 0 &&
        (liveAgentStatus === 'analyzing' ||
            recommendationStatus === 'queued' ||
            recommendationStatus === 'running');
    const agentFailed =
        liveAgentStatus === 'failed' || recommendationStatus === 'failed';
    const shifts = getGeneralOutreachShifts(seed.data, liveData);
    const responseCountParts = getResponseCountParts(seed.data, liveData);
    const status =
        formatGeneralOutreachStatus(getStringValue(liveData, 'status')) ??
        formatGeneralOutreachStatus(getStringValue(seed.data, 'status'));

    // The agent runtime publishes outreach_suggestion_added on every run
    // state sync and outreach_updated when the run settles, so organization
    // events are the fast path for keeping this card fresh. Bursts (e.g. a
    // swarm batch staging many suggestions) collapse into one leading and
    // one trailing refresh per throttle window.
    const refreshOutreach = useCallback(() => {
        void mutateOutreach();
        if (outreachAgentRun === undefined) {
            void mutateOutreachAgentRun();
        }
    }, [mutateOutreach, mutateOutreachAgentRun, outreachAgentRun]);
    const throttledRefreshOutreach = useThrottledCallback(
        refreshOutreach,
        OUTREACH_EVENT_REFRESH_THROTTLE_MS,
    );
    const refreshOutreachForEvent = useCallback(
        (event: OrganizationEvent) => {
            if (
                event.resource.type !== 'outreach' ||
                event.resource.id !== seed.outreachId
            ) {
                return;
            }
            throttledRefreshOutreach();
        },
        [seed.outreachId, throttledRefreshOutreach],
    );
    useListenForOrganizationEvent(
        'outreach_suggestion_added',
        refreshOutreachForEvent,
        { organizationId },
    );
    useListenForOrganizationEvent('outreach_updated', refreshOutreachForEvent, {
        organizationId,
    });
    // Safety net while the agent is still finding caregivers: a dropped
    // websocket or missed event must not strand the card on the spinner, so
    // keep a slow refresh alongside the event-driven fast path.
    useEffect(() => {
        if (!isFindingCaregivers) {
            return;
        }

        const intervalId = window.setInterval(() => {
            void mutateOutreach();
            void mutateOutreachAgentRun();
        }, 30_000);
        return () => window.clearInterval(intervalId);
    }, [isFindingCaregivers, mutateOutreach, mutateOutreachAgentRun]);

    const heading = agentFailed
        ? 'Recommendation check needs review'
        : isFindingCaregivers
          ? 'Finding caregivers'
          : suggestions.length > 0
            ? 'Recommendations ready'
            : seed.source === 'draft'
              ? 'Outreach created'
              : 'Outreach checked';
    const subheading = agentFailed
        ? 'Phoebe couldn’t generate recommendations for this step.'
        : isFindingCaregivers
          ? 'Phoebe is checking availability, distance, overtime, and fit.'
          : suggestions.length > 0
            ? `${suggestions.length} caregiver${suggestions.length === 1 ? '' : 's'} recommended.`
            : 'No caregiver recommendations are available yet.';

    const visibleSuggestions = suggestions.slice(0, 5);
    const card = (
        <div className="border-border/50 bg-background max-w-xl rounded-lg border p-3 text-left break-words">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        {isFindingCaregivers ? (
                            <Loader2 className="text-primary h-4 w-4 animate-spin" />
                        ) : null}
                        <p className="text-foreground text-sm font-medium">
                            {heading}
                        </p>
                        {status ? (
                            <Badge
                                variant="secondary"
                                className="px-1.5 py-0 text-[10px]"
                            >
                                {status}
                            </Badge>
                        ) : null}
                    </div>
                    <p className="text-muted-foreground mt-1 text-xs">
                        {subheading}
                    </p>
                </div>
            </div>
            {shifts.length > 0 ? (
                <div className="border-border/50 mt-3 border-t pt-3">
                    <p className="text-muted-foreground mb-1.5 text-[10px] font-medium tracking-wide uppercase">
                        Shifts
                    </p>
                    <div className="space-y-1">
                        {shifts.slice(0, 3).map((shift, index) => (
                            <p
                                key={`general-outreach-shift-${index}`}
                                className="text-foreground text-xs"
                            >
                                {formatGeneralOutreachShift(shift, timezone)}
                            </p>
                        ))}
                        {shifts.length > 3 ? (
                            <p className="text-muted-foreground text-[10px]">
                                +{shifts.length - 3} more shifts
                            </p>
                        ) : null}
                    </div>
                </div>
            ) : null}
            {visibleSuggestions.length > 0 ? (
                <div className="border-border/50 mt-3 border-t pt-3">
                    <p className="text-muted-foreground mb-2 text-[10px] font-medium tracking-wide uppercase">
                        Recommended caregivers
                    </p>
                    <div className="space-y-2">
                        {visibleSuggestions.map((suggestion, index) => {
                            const caregiverId = getStringValue(
                                suggestion,
                                'caregiver_id',
                            );
                            const caregiverName =
                                getStringValue(suggestion, 'caregiver_name') ??
                                'Unknown caregiver';
                            const rank =
                                getNumberValue(suggestion, 'rank') ?? index + 1;
                            const distance = getNumberValue(
                                suggestion,
                                'distance',
                            );
                            const starRating = getNumberValue(
                                suggestion,
                                'star_rating',
                            );
                            const isOvertime =
                                getStringValue(
                                    suggestion,
                                    'overtime_status',
                                ) === 'overtime';
                            const reasoning = getStringValue(
                                suggestion,
                                'reasoning',
                            );
                            return (
                                <div
                                    key={
                                        caregiverId ??
                                        `general-outreach-suggestion-${rank}`
                                    }
                                    className="space-y-1"
                                >
                                    <div className="flex flex-wrap items-center gap-1.5 text-xs">
                                        <span className="text-muted-foreground text-[10px]">
                                            #{rank}
                                        </span>
                                        {caregiverId && onCaregiverClick ? (
                                            <button
                                                type="button"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    onCaregiverClick(
                                                        caregiverId,
                                                    );
                                                }}
                                                className="text-primary decoration-primary/40 hover:decoration-primary cursor-pointer text-left font-medium underline underline-offset-2"
                                            >
                                                {caregiverName}
                                            </button>
                                        ) : (
                                            <span className="text-foreground font-medium">
                                                {caregiverName}
                                            </span>
                                        )}
                                        {distance !== null ? (
                                            <span className="text-muted-foreground">
                                                {distance.toFixed(1)} mi
                                            </span>
                                        ) : null}
                                        {starRating !== null ? (
                                            <span className="text-muted-foreground">
                                                {starRating.toFixed(1)} stars
                                            </span>
                                        ) : null}
                                        {isOvertime ? (
                                            <Badge
                                                variant="secondary"
                                                className="px-1.5 py-0 text-[10px]"
                                            >
                                                Overtime
                                            </Badge>
                                        ) : null}
                                    </div>
                                    {reasoning ? (
                                        <p className="text-muted-foreground line-clamp-2 text-xs">
                                            {reasoning}
                                        </p>
                                    ) : null}
                                </div>
                            );
                        })}
                        {suggestions.length > visibleSuggestions.length ? (
                            <p className="text-muted-foreground text-[10px]">
                                +
                                {suggestions.length - visibleSuggestions.length}{' '}
                                more recommendations
                            </p>
                        ) : null}
                    </div>
                </div>
            ) : null}
            {responseCountParts.length > 0 ? (
                <div className="border-border/50 mt-3 flex flex-wrap gap-1.5 border-t pt-3">
                    {responseCountParts.map((part) => (
                        <Badge
                            key={part}
                            variant="secondary"
                            className="px-1.5 py-0 text-[10px]"
                        >
                            {part}
                        </Badge>
                    ))}
                </div>
            ) : null}
            {outreachAgentRunState.timelineEntries.length > 0 ? (
                <div className="border-border/50 mt-3 border-t pt-3">
                    <PhoebeAgentTimelineEntries
                        entries={outreachAgentRunState.timelineEntries}
                        onCaregiverClick={onCaregiverClick}
                        shouldShowWorkflowActivitySurface={true}
                        currentRunEventStreamIsGenerating={
                            outreachAgentRunState.currentRunEventStreamIsGenerating
                        }
                    />
                </div>
            ) : null}
            {error ? (
                <p className="text-destructive mt-3 text-xs">{error}</p>
            ) : null}
        </div>
    );

    if (nested) {
        return card;
    }

    return <div className="flex justify-start">{card}</div>;
}

type ExecutionPlanItemStatus = 'pending' | 'in_progress' | 'completed';

type ExecutionPlanItem = {
    readonly id: string;
    readonly title: string;
    readonly status: ExecutionPlanItemStatus;
    readonly subtitle?: string | null;
    readonly receipt?: ProjectedRunMilestoneReceipt | null;
    readonly durationLabel?: string | null;
    readonly fanInState?: NormalizedDeclaredExecutionPlanFanInState;
};

type ExecutionPlanData = {
    readonly items: ReadonlyArray<ExecutionPlanItem>;
};

type ExecutionPlanRender = {
    readonly latestPlan: ExecutionPlanData | null;
    readonly source: 'declared' | 'projected' | null;
};

export function MonitoringConfigurationCardForRun({
    run,
    controls,
    monitoringScheduleMetadata,
}: {
    readonly run: StreamRunState | null;
    readonly controls: MonitoringActivityControls | null;
    readonly monitoringScheduleMetadata?: Record<string, unknown> | null;
}) {
    if (controls === null) {
        return null;
    }

    // The card's conditions come from monitoringScheduleMetadata, so it
    // renders for auto-monitored outreaches that never produced a
    // monitor_outreach tool call. Tool nodes are only a fallback condition
    // source for tool-driven (manual) monitoring.
    const monitoringToolNodes = run ? getMonitoringToolNodesFromRun(run) : [];

    return (
        <MonitoringConfigurationCard
            tools={monitoringToolNodes}
            controls={controls}
            monitoringScheduleMetadata={monitoringScheduleMetadata}
        />
    );
}

export function getLatestMonitoringRun(
    entries: ReadonlyArray<RenderTimelineEntry>,
    streamOverlayRun: StreamRunState | null,
): StreamRunState | null {
    let result: StreamRunState | null = null;

    for (const entry of entries) {
        if (
            entry.kind === 'completed-run' &&
            getMonitoringToolNodesFromRun(entry.run).length > 0
        ) {
            result = entry.run;
        }
    }

    if (
        streamOverlayRun !== null &&
        getMonitoringToolNodesFromRun(streamOverlayRun).length > 0
    ) {
        result = streamOverlayRun;
    }

    return result;
}

function getExecutionPlanChecklistStatus(
    item: ExecutionPlanItem,
    runStatus: StreamRunState['status'],
): ExecutionPlanChecklistStatus {
    const status = item.status;
    if (status === 'completed') {
        return 'completed';
    }
    if (status === 'pending') {
        return 'pending';
    }
    if (runStatus === 'errored') {
        if (item.fanInState !== undefined) {
            return item.fanInState === 'completed'
                ? 'completed'
                : 'in_progress';
        }
        return 'error';
    }
    return runStatus === 'running' ? 'in_progress' : 'completed';
}

function buildExecutionPlanChecklistItems({
    plan,
    runStatus,
    parseFailed,
}: {
    readonly plan: ExecutionPlanData;
    readonly runStatus: StreamRunState['status'];
    readonly parseFailed: boolean;
}): Array<ProjectedRunMilestoneItem> {
    const items = plan.items.map((item) => {
        return {
            id: item.id,
            title: item.title,
            status: getExecutionPlanChecklistStatus(item, runStatus),
            subtitle: item.subtitle,
            receipt: item.receipt,
            durationLabel: item.durationLabel,
        };
    });

    if (!parseFailed) {
        return items;
    }

    return [
        ...items,
        {
            id: 'execution-plan-parse-failed',
            title: 'Last plan update needs review',
            status: 'error',
        },
    ];
}

function ExecutionPlanTraceCard({
    plan,
    nested,
    runStatus,
    parseFailed,
    source,
    defaultExpanded,
}: {
    readonly plan: ExecutionPlanData;
    readonly nested: boolean;
    readonly runStatus: StreamRunState['status'];
    readonly parseFailed: boolean;
    readonly source: 'declared' | 'projected';
    readonly defaultExpanded?: boolean;
}) {
    const items = buildExecutionPlanChecklistItems({
        plan,
        runStatus,
        parseFailed,
    });
    const card =
        source === 'projected' ? (
            <ProjectedRunMilestones
                items={items}
                runStatus={runStatus}
                defaultExpanded={defaultExpanded}
            />
        ) : (
            <ExecutionPlanChecklist
                items={items}
                className={nested ? undefined : 'rounded-lg border p-3'}
            />
        );
    if (nested) {
        return card;
    }
    return <div className="flex justify-start">{card}</div>;
}

function getProjectedMilestoneReceipt(
    tools: ReadonlyArray<StreamRunNode>,
): ProjectedRunMilestoneReceipt | null {
    const tool = tools[tools.length - 1];
    if (
        tool?.toolName !== QUERY_TOOL_NAME ||
        !tool.isComplete ||
        tool.error !== null ||
        !isRecord(tool.result)
    ) {
        return null;
    }
    const rowCount = getNumberValue(tool.result, 'row_count');
    if (rowCount === null || !Number.isSafeInteger(rowCount) || rowCount < 0) {
        return null;
    }

    const columns = tool.result['columns'];
    const fieldCount =
        Array.isArray(columns) &&
        columns.every((column) => typeof column === 'string')
            ? columns.length
            : undefined;
    const hasRowsBeyondLimit = Object.prototype.hasOwnProperty.call(
        tool.result,
        'rows_beyond_limit',
    );
    const rawRowsBeyondLimit = tool.result['rows_beyond_limit'];
    const omittedRowCount =
        hasRowsBeyondLimit && rawRowsBeyondLimit === null
            ? null
            : typeof rawRowsBeyondLimit === 'number' &&
                Number.isSafeInteger(rawRowsBeyondLimit) &&
                rawRowsBeyondLimit >= 0
              ? rawRowsBeyondLimit
              : undefined;

    return {
        rowCount,
        ...(fieldCount === undefined ? {} : { fieldCount }),
        ...(omittedRowCount === undefined ? {} : { omittedRowCount }),
    };
}

function getProjectedMilestoneDurationLabel(
    tools: ReadonlyArray<StreamRunNode>,
): string | null {
    if (tools.length === 0) {
        return null;
    }
    let totalDurationMs = 0;
    for (const tool of tools) {
        if (
            tool.durationMs === null ||
            tool.durationMs === undefined ||
            !Number.isFinite(tool.durationMs) ||
            tool.durationMs < 0
        ) {
            return null;
        }
        totalDurationMs += tool.durationMs;
    }
    return formatToolExecutionDuration(totalDurationMs);
}

export function ToolExecutionCard({
    entry,
    nested = false,
    showActiveLabel = false,
    onCaregiverClick,
    onClientClick,
    showGeneralOutreachProgress = false,
    subagentDraftProgress = null,
    renderToolArtifacts,
    renderToolOutput,
    onInspectToolEntry,
    isInspected = false,
    presentation = 'default',
    useRawToolName = false,
}: {
    entry: ToolExecutionEntry;
    nested?: boolean;
    showActiveLabel?: boolean;
    onCaregiverClick?: (caregiverId: string) => void;
    onClientClick?: (clientId: string) => void;
    showGeneralOutreachProgress?: boolean;
    subagentDraftProgress?: SubagentDraftProgress | null;
    renderToolArtifacts?: ToolArtifactRenderer;
    renderToolOutput?: ToolOutputRenderer;
    onInspectToolEntry?: ToolEntryInspectorHandler;
    isInspected?: boolean;
    presentation?: ToolCardPresentation;
    // Label the card with the raw tool name (e.g. "query_db") instead of
    // the normalized English label. Used by the grouped-activity view
    // where operators want exact tool identifiers.
    useRawToolName?: boolean;
}) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [hasAutoExpandedInlineOutput, setHasAutoExpandedInlineOutput] =
        useState(false);
    const dynamicWidgetBuildContext = useDynamicWidgetBuildContext();
    const promptAction = useDynamicWidgetPromptAction();
    const getActionState = useDynamicWidgetActionStateResolver();
    const generalOutreachProgressSeed = showGeneralOutreachProgress
        ? getGeneralOutreachProgressSeed(entry)
        : null;
    const visuallyComplete = entry.isComplete && !showActiveLabel;
    const isStreamingSubagentDrafts =
        entry.name === 'get_outreach_suggestion_drafts' &&
        !visuallyComplete &&
        subagentDraftProgress !== null;

    useEffect(() => {
        if (isStreamingSubagentDrafts) {
            setIsExpanded(true);
        }
    }, [isStreamingSubagentDrafts]);

    // Show "active" label until the next tool/turn appears
    const approvalBadge = getToolApprovalBadge(entry);
    const reviewMessage = getToolExecutionReviewMessage(entry);
    const labelIsComplete = shouldUseCompletedToolExecutionLabel(
        entry,
        visuallyComplete,
    );
    const title = useRawToolName ? (
        <span className="font-mono">{entry.name}</span>
    ) : entry.name === 'monitor_outreach' ? (
        renderMonitoringToolLabel({
            isComplete: labelIsComplete,
        })
    ) : (
        renderToolLabel(
            formatToolName(
                entry.name,
                labelIsComplete,
                entry.arguments,
                labelIsComplete ? entry.output : undefined,
            ),
            entry.arguments,
            onCaregiverClick,
            labelIsComplete ? entry.output : undefined,
        )
    );
    const ToolIcon = getToolIcon(entry.name);
    const shellStatus = getToolExecutionShellStatus({
        isComplete: visuallyComplete,
        error: reviewMessage,
    });
    const durationLabel = formatToolExecutionDuration(entry.durationMs);
    const shouldShowInputsForTool = shouldShowToolInputsForTool(entry.name);
    const artifactContent = renderToolArtifacts?.(entry) ?? null;
    const renderedToolOutput = !entry.error
        ? (renderToolOutput?.(entry) ?? null)
        : null;
    const customToolOutputPayload = isToolOutputRendererPayload(
        renderedToolOutput,
    )
        ? renderedToolOutput
        : null;
    const customOutputContent: ReactNode | null =
        customToolOutputPayload !== null
            ? customToolOutputPayload.content
            : (renderedToolOutput as ReactNode | null);
    const dynamicWidgetModel =
        dynamicWidgetBuildContext !== null &&
        customOutputContent === null &&
        entry.isComplete
            ? buildDynamicWidget(entry, dynamicWidgetBuildContext)
            : null;
    const classicDynamicWidgetSummary =
        customOutputContent === null && visuallyComplete
            ? formatToolOutput(
                  entry.name,
                  entry.output,
                  onCaregiverClick,
                  onClientClick,
                  entry.arguments,
              )
            : null;
    const dynamicWidgetContent =
        dynamicWidgetModel === null
            ? null
            : renderDynamicWidget(
                  dynamicWidgetModel,
                  {
                      onCaregiverClick,
                      onClientClick,
                      ...(promptAction !== null
                          ? {
                                onWidgetAction: promptAction,
                                onPromptAction: promptAction,
                            }
                          : {}),
                      ...(getActionState !== null ? { getActionState } : {}),
                  },
                  <DynamicWidgetClassicFallback
                      summary={classicDynamicWidgetSummary}
                      output={entry.output}
                  />,
              );
    const suppressLegacyText =
        customOutputContent !== null &&
        (customToolOutputPayload === null ||
            customToolOutputPayload.suppressText !== false);
    const shouldDefaultExpandCustomOutput =
        customToolOutputPayload?.defaultExpanded ?? true;
    const summary =
        customOutputContent !== null
            ? null
            : dynamicWidgetContent !== null
              ? {
                    items: [
                        {
                            label: '',
                            value: dynamicWidgetContent,
                        },
                    ],
                }
              : visuallyComplete
                ? classicDynamicWidgetSummary
                : isStreamingSubagentDrafts
                  ? buildStreamingSubagentDraftSummary(subagentDraftProgress)
                  : null;
    const outputReviewMessage = entry.error ? null : reviewMessage;
    const shouldShowOutputReviewCard =
        outputReviewMessage !== null && summary === null;
    const hasInspectableToolOutput =
        (entry.output !== null && entry.output !== undefined) ||
        entry.text !== null;
    const hasInspectableToolInput =
        entry.arguments !== null && entry.arguments !== undefined;
    const hasInspectableToolPayload =
        hasInspectableToolOutput || hasInspectableToolInput;
    const canInspectToolEntry =
        presentation === 'minimal' &&
        onInspectToolEntry !== undefined &&
        hasInspectableToolPayload;
    const inspectToolEntry = canInspectToolEntry
        ? () => onInspectToolEntry?.(entry)
        : undefined;
    const usesInspectorOnly =
        canInspectToolEntry &&
        !isStreamingSubagentDrafts &&
        customOutputContent === null;
    const redactText = useRedactedText();
    // The collapsed subtitle is visible without expanding, and several
    // tools put a bare record id there (header_details.ts).
    const rawHeaderDetail =
        getToolExecutionHeaderDetail(entry) ??
        customToolOutputPayload?.collapsedDetail ??
        null;
    const headerDetail =
        rawHeaderDetail === null ? null : redactText(rawHeaderDetail);

    // Dynamic widgets are the answer, not auxiliary tool detail — the
    // mockup contract is that the answer carries the widget's controls, so
    // a settled widget-bearing card must not hide them behind the
    // disclosure (reload lands on the settled path too).
    const hasDynamicWidget = dynamicWidgetModel !== null;
    useEffect(() => {
        if (
            hasAutoExpandedInlineOutput ||
            (!hasDynamicWidget &&
                (customOutputContent === null ||
                    presentation !== 'minimal' ||
                    !shouldDefaultExpandCustomOutput))
        ) {
            return;
        }
        setHasAutoExpandedInlineOutput(true);
        setIsExpanded(true);
    }, [
        customOutputContent,
        hasAutoExpandedInlineOutput,
        hasDynamicWidget,
        presentation,
        shouldDefaultExpandCustomOutput,
    ]);

    if (generalOutreachProgressSeed) {
        return (
            <GeneralOutreachProgressCard
                seed={generalOutreachProgressSeed}
                nested={nested}
                onCaregiverClick={onCaregiverClick}
                error={entry.error}
            />
        );
    }

    if (entry.name === QUERY_TOOL_NAME && !useRawToolName) {
        const queryRow = (
            <div className="space-y-1.5">
                <QueryToolRow
                    label={formatToolName(
                        entry.name,
                        labelIsComplete,
                        entry.arguments,
                    )}
                    icon={
                        visuallyComplete ? (
                            ToolIcon ? (
                                <ToolIcon className="h-3.5 w-3.5" />
                            ) : null
                        ) : (
                            <Spinner size="sm" />
                        )
                    }
                />
                {entry.error ? (
                    <ToolIssueCard
                        title="This step needs review"
                        error={entry.error}
                    />
                ) : null}
            </div>
        );
        if (nested) {
            return queryRow;
        }
        return <div className="flex justify-start">{queryRow}</div>;
    }

    // In the minimal presentation, rendered artifacts are the result —
    // the generic result card would duplicate the same payload as noise.
    const showGenericResult =
        customOutputContent === null &&
        !usesInspectorOnly &&
        !shouldShowOutputReviewCard &&
        entry.output !== null &&
        entry.output !== undefined &&
        !(presentation === 'minimal' && artifactContent !== null);
    const isOpen = usesInspectorOnly
        ? false
        : isExpanded || isStreamingSubagentDrafts;
    const alignWithMessages =
        presentation === 'minimal' && entry.name === 'save_note';
    const shell = (
        <ToolExecutionCardShell
            title={title}
            toolName={entry.name}
            icon={ToolIcon}
            status={shellStatus}
            durationLabel={durationLabel}
            approvalBadge={approvalBadge}
            detail={headerDetail}
            isOpen={isOpen}
            onToggle={() => {
                if (usesInspectorOnly) {
                    inspectToolEntry?.();
                    return;
                }
                setIsExpanded(!isOpen);
            }}
            nested={nested || alignWithMessages}
            isSelected={isInspected}
            usesInspector={usesInspectorOnly}
            presentation={presentation}
            fullWidth={dynamicWidgetContent !== null}
        >
            {dynamicWidgetBuildContext !== null &&
            customOutputContent === null &&
            entry.isComplete &&
            dynamicWidgetModel === null ? (
                <DynamicWidgetFallbackMetric entry={entry} />
            ) : null}
            {entry.error ? (
                <ToolIssueCard
                    title="This step needs review"
                    error={entry.error}
                />
            ) : null}
            {shouldShowOutputReviewCard ? (
                <ToolIssueCard
                    title="This step needs review"
                    error={outputReviewMessage}
                />
            ) : null}
            {summary ? (
                <div className="space-y-2.5">
                    {summary.items.map((item, index) =>
                        item.label === '' ? (
                            <div
                                key={`${item.label}-${index}`}
                                className="text-foreground text-xs"
                            >
                                {item.value}
                            </div>
                        ) : (
                            <div
                                key={`${item.label}-${index}`}
                                className="flex gap-3"
                            >
                                <span className="text-muted-foreground min-w-[4.5rem] flex-shrink-0 text-xs font-medium">
                                    {item.label}
                                </span>
                                {typeof item.value === 'string' ? (
                                    <span className="text-foreground flex-1 text-xs">
                                        {item.value}
                                    </span>
                                ) : (
                                    <div className="text-foreground flex-1 text-xs">
                                        {item.value}
                                    </div>
                                )}
                            </div>
                        ),
                    )}
                </div>
            ) : (
                <div className="space-y-2">
                    {entry.text &&
                    (customOutputContent === null || !suppressLegacyText) ? (
                        <div className="space-y-1">
                            {presentation === 'minimal' ? null : (
                                <p className="text-muted-foreground text-xs font-medium">
                                    Result
                                </p>
                            )}
                            <Markdown
                                className={cn(
                                    'text-foreground text-xs',
                                    inspectToolEntry && 'line-clamp-6',
                                )}
                            >
                                {redactText(entry.text ?? '')}
                            </Markdown>
                            {inspectToolEntry &&
                            !usesInspectorOnly &&
                            !showGenericResult ? (
                                <button
                                    type="button"
                                    onClick={inspectToolEntry}
                                    className="mt-1 cursor-pointer text-xs font-medium text-emerald-700 transition-colors hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
                                >
                                    Open full output
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                    {showGenericResult ? (
                        <GenericToolResultCard
                            value={entry.output}
                            emptyText="No result"
                            presentation={presentation}
                            onInspect={inspectToolEntry}
                            inspectLabel="Open full output"
                        />
                    ) : null}
                    {customOutputContent}
                </div>
            )}
            {artifactContent ? (
                <div className="border-border/30 border-t pt-2">
                    {artifactContent}
                </div>
            ) : null}
            {inspectToolEntry &&
            !usesInspectorOnly &&
            !showGenericResult &&
            entry.text === null &&
            hasInspectableToolOutput ? (
                <button
                    type="button"
                    onClick={inspectToolEntry}
                    className="cursor-pointer text-xs font-medium text-emerald-700 transition-colors hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
                >
                    Open full output
                </button>
            ) : null}
            {/* A dynamic widget IS the tool's presentation; the raw Inputs
                breakdown under it reads as debug output (still reachable via
                the inspector). */}
            {shouldShowInputsForTool && dynamicWidgetModel === null ? (
                <div className="border-border/30 border-t pt-2">
                    <ToolValueSection
                        title="Inputs"
                        value={entry.arguments}
                        emptyText="No inputs"
                    />
                </div>
            ) : null}
        </ToolExecutionCardShell>
    );
    return shell;
}

function ThoughtTraceCard({
    entry,
    isActive = false,
    showDurationLabels = true,
    animateContent = false,
    onTypewriterProgress,
    decidingCallIds,
    answeringQuestionCallIds,
    onDecision,
    onQuestionAnswer,
    onCaregiverClick,
    onClientClick,
    showGeneralOutreachProgressCards = false,
    subagentDraftProgress = null,
    renderToolArtifacts,
    renderToolOutput,
    onInspectToolEntry,
    selectedToolEntryId = null,
    toolCardPresentation = 'default',
    organizationName = null,
    showTrainingModeQuestionContext = false,
    trainingModeContextReady = true,
    useRawToolNames = false,
    showPlainTextContent = false,
}: {
    entry: ThoughtEntry;
    isActive?: boolean;
    showDurationLabels?: boolean;
    animateContent?: boolean;
    onTypewriterProgress?: () => void;
    decidingCallIds: Record<string, boolean>;
    answeringQuestionCallIds: Record<string, boolean>;
    onDecision?: (
        callId: string,
        decision: ToolApprovalDecision,
        alwaysAllowScope?: ToolApprovalScope,
        argumentsOverride?: Record<string, unknown>,
    ) => Promise<void>;
    onQuestionAnswer?: (
        callId: string,
        payload: QuestionAnswerPayload,
    ) => Promise<void>;
    onCaregiverClick?: (caregiverId: string) => void;
    onClientClick?: (clientId: string) => void;
    showGeneralOutreachProgressCards?: boolean;
    subagentDraftProgress?: SubagentDraftProgress | null;
    renderToolArtifacts?: ToolArtifactRenderer;
    renderToolOutput?: ToolOutputRenderer;
    onInspectToolEntry?: ToolEntryInspectorHandler;
    selectedToolEntryId?: string | null;
    toolCardPresentation?: ToolCardPresentation;
    organizationName?: string | null;
    showTrainingModeQuestionContext?: boolean;
    // See UserQuestionCard's `trainingModeContextReady` — threaded through
    // unchanged to the nested question card below.
    trainingModeContextReady?: boolean;
    useRawToolNames?: boolean;
    // Grouped-activity view: show the thought text directly (no collapsed
    // "Thought" toggle row, no card chrome) — the surrounding activity
    // group already provides the collapse affordance.
    showPlainTextContent?: boolean;
}) {
    const [isExpanded, setIsExpanded] = useState(isActive);
    const shouldAnimateContent = useLatchedAnimationEligibility(animateContent);
    const renderedContent = useTypewriterText(entry.content, {
        animate: shouldAnimateContent,
        animationKey: entry.id,
        onRevealFrame: shouldAnimateContent ? onTypewriterProgress : undefined,
    });
    // Training mode never shows raw thinking text: it narrates internal
    // machinery (field names, skills, tool plans) that schedulers must not
    // see. Tool rows still render; only the thought prose is dropped.
    const hasThoughtContent =
        !showTrainingModeQuestionContext && entry.content.trim().length > 0;
    const usesWorkbenchPresentation = toolCardPresentation === 'minimal';
    // Silent internal tool calls (e.g. feature_gap feedback) render nothing,
    // so a thought whose only tools are silent has no visible content and
    // must not render a phantom empty wrapper.
    const hasVisibleTool = entry.tools.some(
        (toolEntry) =>
            !isSilentInternalToolCall(toolEntry.name, toolEntry.arguments),
    );

    useEffect(() => {
        if (isActive) {
            setIsExpanded(true);
        }
    }, [isActive]);

    if (!hasThoughtContent && !hasVisibleTool) {
        return null;
    }

    const title = usesWorkbenchPresentation
        ? isActive
            ? 'Thinking'
            : 'Agent activity'
        : isActive
          ? 'Thinking'
          : showDurationLabels
            ? formatThoughtTitle(entry.durationMs)
            : 'Thought';
    const renderNestedToolEntry = (toolEntry: ToolExecutionEntry) => {
        if (isSilentInternalToolCall(toolEntry.name, toolEntry.arguments)) {
            return null;
        }
        if (isUserQuestionToolEntry(toolEntry)) {
            return (
                <UserQuestionCard
                    key={toolEntry.id}
                    entry={toolEntry}
                    nested={true}
                    resolved={toolEntry.isComplete}
                    isSubmitting={
                        toolEntry.callId
                            ? (answeringQuestionCallIds[toolEntry.callId] ??
                              false)
                            : false
                    }
                    onSubmit={
                        toolEntry.callId &&
                        onQuestionAnswer &&
                        !toolEntry.isComplete
                            ? async (payload) => {
                                  await onQuestionAnswer(
                                      toolEntry.callId ?? '',
                                      payload,
                                  );
                              }
                            : undefined
                    }
                    onCancel={
                        toolEntry.callId &&
                        onQuestionAnswer &&
                        !toolEntry.isComplete
                            ? async () => {
                                  await onQuestionAnswer(
                                      toolEntry.callId ?? '',
                                      {
                                          cancelled: true,
                                      },
                                  );
                              }
                            : undefined
                    }
                    showTrainingModeContext={
                        showTrainingModeQuestionContext &&
                        isTeachPhoebeUserQuestion(toolEntry)
                    }
                    trainingModeContextReady={trainingModeContextReady}
                />
            );
        }
        if (toolEntry.status === 'pending_approval' && !toolEntry.isComplete) {
            return (
                <ToolApprovalCard
                    key={toolEntry.id}
                    entry={toolEntry}
                    nested={true}
                    isSubmitting={
                        toolEntry.callId
                            ? (decidingCallIds[toolEntry.callId] ?? false)
                            : false
                    }
                    onDecision={
                        toolEntry.callId && onDecision
                            ? async (
                                  decision,
                                  alwaysAllowScope,
                                  argumentsOverride,
                              ) => {
                                  if (argumentsOverride === undefined) {
                                      await onDecision(
                                          toolEntry.callId ?? '',
                                          decision,
                                          alwaysAllowScope,
                                      );
                                  } else {
                                      await onDecision(
                                          toolEntry.callId ?? '',
                                          decision,
                                          alwaysAllowScope,
                                          argumentsOverride,
                                      );
                                  }
                              }
                            : undefined
                    }
                    organizationName={organizationName}
                />
            );
        }
        return (
            <ToolExecutionCard
                key={toolEntry.id}
                entry={toolEntry}
                nested={true}
                onCaregiverClick={onCaregiverClick}
                onClientClick={onClientClick}
                showGeneralOutreachProgress={showGeneralOutreachProgressCards}
                subagentDraftProgress={subagentDraftProgress}
                renderToolArtifacts={renderToolArtifacts}
                renderToolOutput={renderToolOutput}
                onInspectToolEntry={onInspectToolEntry}
                isInspected={
                    selectedToolEntryId === getToolEntryInspectionId(toolEntry)
                }
                presentation={toolCardPresentation}
                useRawToolName={useRawToolNames}
            />
        );
    };
    const toolRows = hasVisibleTool ? (
        <div
            className={cn(
                'space-y-1.5',
                isExpanded && hasThoughtContent ? 'mt-3' : 'mt-1',
            )}
        >
            {entry.tools.map(renderNestedToolEntry)}
        </div>
    ) : null;
    const shouldShowToolRowsOutsideThought =
        usesWorkbenchPresentation && (!isExpanded || !hasThoughtContent);

    if (showPlainTextContent) {
        return (
            <div className="w-full">
                {hasThoughtContent ? (
                    <InterstitialThinkingText content={renderedContent} />
                ) : null}
                {toolRows}
            </div>
        );
    }

    if (!usesWorkbenchPresentation && !hasThoughtContent) {
        return toolRows ? <div className="w-full">{toolRows}</div> : null;
    }

    if (usesWorkbenchPresentation && !hasThoughtContent) {
        return toolRows ? (
            <div className="relative ml-7 w-[calc(100%-1.75rem)] max-w-full">
                {toolRows}
            </div>
        ) : null;
    }

    return (
        <div
            className={cn(
                'flex justify-start',
                usesWorkbenchPresentation
                    ? 'relative ml-7 w-[calc(100%-1.75rem)]'
                    : 'w-full',
            )}
        >
            {usesWorkbenchPresentation ? (
                <PhoebeAgentMark className="absolute top-2 -left-7 size-5" />
            ) : null}
            <div
                className={cn(
                    'text-muted-foreground w-full max-w-full text-left text-xs break-words',
                    usesWorkbenchPresentation &&
                        'rounded-[7px] border border-[#DBE3E3] bg-[#FFFFFF]/92 px-3 py-2.5 shadow-[0_1px_0_rgba(0,36,41,0.04)]',
                )}
            >
                {hasThoughtContent ? (
                    <LiveStatusRow
                        status={{
                            phase: 'thinking',
                            label: title,
                            detail: null,
                            toolName: null,
                        }}
                        isExpandable={true}
                        isExpanded={isExpanded}
                        onToggle={() => setIsExpanded(!isExpanded)}
                        showSpinner={false}
                        presentation={
                            usesWorkbenchPresentation ? 'workbench' : 'default'
                        }
                    />
                ) : null}
                {isExpanded && hasThoughtContent ? (
                    <div
                        className={cn(
                            'mt-2 border-l pl-3',
                            usesWorkbenchPresentation &&
                                'border-[#DBE3E3] pl-3.5',
                        )}
                    >
                        <Markdown
                            className="space-y-4"
                            components={{
                                p: ({ node, ...props }) => (
                                    <p
                                        className="mb-4 text-xs last:mb-0"
                                        {...props}
                                    />
                                ),
                                strong: ({ node, ...props }) => (
                                    <strong
                                        className="font-semibold"
                                        {...props}
                                    />
                                ),
                                em: ({ node, ...props }) => (
                                    <em className="italic" {...props} />
                                ),
                            }}
                        >
                            {renderedContent}
                        </Markdown>
                        {toolRows}
                        {isActive && (
                            <span className="ml-0.5 inline-flex">
                                <span className="animate-pulse">.</span>
                                <span
                                    className="animate-pulse"
                                    style={{ animationDelay: '0.2s' }}
                                >
                                    .
                                </span>
                                <span
                                    className="animate-pulse"
                                    style={{ animationDelay: '0.4s' }}
                                >
                                    .
                                </span>
                            </span>
                        )}
                    </div>
                ) : null}
                {shouldShowToolRowsOutsideThought ? toolRows : null}
            </div>
        </div>
    );
}

type PhoebeAgentTimelineEntriesProps = {
    readonly entries: Array<RenderTimelineEntry>;
    readonly recommendationOrganizationId?: string | null;
    readonly canOpenSubagentRun?: (childRunId: string) => boolean;
    readonly debugPanelOpen?: boolean;
    readonly latestAnimatedAssistantMessageId?: string | null;
    readonly latestAnimatedThoughtId?: string | null;
    readonly onTypewriterProgress?: () => void;
    readonly onCaregiverClick?: (caregiverId: string) => void;
    readonly onClientClick?: (clientId: string) => void;
    readonly onOpenSubagentRun?: (childRunId: string) => void;
    readonly onAssistantPromptAction?: AssistantPromptAction;
    readonly actionStateEntries?: ReadonlyArray<RenderTimelineEntry>;
    readonly onRetryAfterRunError?: () => void;
    readonly isRetryDisabled?: boolean;
    readonly shiftSuggestionTabs?: {
        readonly tabs: Array<ShiftTab>;
        readonly turnSequence: number;
    } | null;
    readonly decidingCallIds?: Record<string, boolean>;
    readonly answeringQuestionCallIds?: Record<string, boolean>;
    readonly onToolDecision?: (
        target: ToolApprovalDecisionTarget,
        decision: ToolApprovalDecision,
        alwaysAllowScope?: ToolApprovalScope,
        argumentsOverride?: Record<string, unknown>,
    ) => Promise<void>;
    readonly onQuestionAnswer?: (
        callId: string,
        payload: QuestionAnswerPayload,
    ) => Promise<void>;
    readonly agentThinking?: boolean;
    readonly streamOverlayRun?: StreamRunState | null;
    readonly timelineNowMs?: number;
    readonly shouldUsePhoebeEventRead?: boolean;
    readonly currentRunEventStreamIsGenerating?: boolean;
    readonly isActiveRunWithoutStream?: boolean;
    readonly manualLiveStatus?: LiveRunStatus | null;
    readonly isPriorAssistantMessageAnimating?: boolean;
    readonly shouldShowWorkflowActivitySurface?: boolean;
    readonly expandedLiveRunId?: string | null;
    readonly onExpandedLiveRunIdChange?: (runId: string | null) => void;
    readonly autoExpandDismissedRunId?: string | null;
    readonly onAutoExpandDismissedRunIdChange?: (runId: string | null) => void;
    readonly showGeneralOutreachProgressCards?: boolean;
    readonly showRecommendationTraceProgress?: boolean;
    readonly showInlineWorkflowActivitySurface?: boolean;
    readonly showCaregiverDecisionProgress?: boolean;
    readonly showThinkingTips?: boolean;
    readonly subagentDraftProgress?: SubagentDraftProgress | null;
    readonly shiftActionPolicy?: ShiftActionPolicy;
    readonly renderToolArtifacts?: ToolArtifactRenderer;
    readonly renderMessageArtifacts?: MessageArtifactRenderer;
    readonly renderToolOutput?: ToolOutputRenderer;
    readonly onInspectToolEntry?: ToolEntryInspectorHandler;
    readonly selectedToolEntryId?: string | null;
    readonly toolCardPresentation?: ToolCardPresentation;
    readonly organizationName?: string | null;
    readonly showTrainingModeQuestionContext?: boolean;
    // See UserQuestionCard's `trainingModeContextReady` — threaded through
    // unchanged to every nested question card.
    readonly trainingModeContextReady?: boolean;
    readonly renderAssistantMessageActions?: AssistantMessageActionRenderer;
    readonly renderMessageFeedback?: AssistantMessageActionRenderer;
    // Opt-in progressive disclosure: everything between the user's query
    // and the final answer (thoughts, tool executions, interstitial
    // assistant narration) collapses into one per-turn summary row like
    // "2 thinking, 6 tool calls" that expands to the underlying entries,
    // with tool sub-rows labeled by raw tool name. Interactive entries
    // (user questions, pending approvals) never group; emitted artifacts
    // stay in the main transcript.
    readonly groupActivityEntries?: boolean;
    readonly highlightedAgentItemId?: string | null;
};

export const EMPTY_DECIDING_CALL_IDS: Record<string, boolean> = {};
const EMPTY_ANSWERING_QUESTION_CALL_IDS: Record<string, boolean> = {};
const EMPTY_INTERSTITIAL_MESSAGE_IDS: ReadonlySet<string> = new Set();

export function PhoebeAgentTimelineEntries({
    entries,
    recommendationOrganizationId,
    canOpenSubagentRun,
    debugPanelOpen = false,
    latestAnimatedAssistantMessageId = null,
    latestAnimatedThoughtId = null,
    onTypewriterProgress,
    onCaregiverClick,
    onClientClick,
    onOpenSubagentRun,
    onAssistantPromptAction,
    actionStateEntries,
    onRetryAfterRunError,
    isRetryDisabled = false,
    shiftSuggestionTabs = null,
    decidingCallIds = EMPTY_DECIDING_CALL_IDS,
    answeringQuestionCallIds = EMPTY_ANSWERING_QUESTION_CALL_IDS,
    onToolDecision,
    onQuestionAnswer,
    agentThinking = false,
    streamOverlayRun = null,
    timelineNowMs = Date.now(),
    shouldUsePhoebeEventRead = true,
    currentRunEventStreamIsGenerating = false,
    isActiveRunWithoutStream = false,
    manualLiveStatus = null,
    isPriorAssistantMessageAnimating = false,
    shouldShowWorkflowActivitySurface = false,
    expandedLiveRunId,
    onExpandedLiveRunIdChange,
    autoExpandDismissedRunId,
    onAutoExpandDismissedRunIdChange,
    showGeneralOutreachProgressCards = false,
    showRecommendationTraceProgress = false,
    showInlineWorkflowActivitySurface = false,
    showCaregiverDecisionProgress = false,
    showThinkingTips = false,
    subagentDraftProgress = null,
    shiftActionPolicy = DEFAULT_SHIFT_ACTION_POLICY,
    renderToolArtifacts,
    renderMessageArtifacts,
    renderToolOutput,
    onInspectToolEntry,
    selectedToolEntryId = null,
    toolCardPresentation = 'default',
    organizationName = null,
    showTrainingModeQuestionContext = false,
    trainingModeContextReady = true,
    renderAssistantMessageActions,
    renderMessageFeedback,
    groupActivityEntries = false,
    highlightedAgentItemId = null,
}: PhoebeAgentTimelineEntriesProps) {
    const dynamicWidgetsEnabled = useDynamicWidgetBuildContext() !== null;
    const [internalExpandedLiveRunId, setInternalExpandedLiveRunId] = useState<
        string | null
    >(null);
    const [
        internalAutoExpandDismissedRunId,
        setInternalAutoExpandDismissedRunId,
    ] = useState<string | null>(null);
    const resolvedExpandedLiveRunId =
        expandedLiveRunId === undefined
            ? internalExpandedLiveRunId
            : expandedLiveRunId;
    const resolvedAutoExpandDismissedRunId =
        autoExpandDismissedRunId === undefined
            ? internalAutoExpandDismissedRunId
            : autoExpandDismissedRunId;
    const setResolvedExpandedLiveRunId =
        onExpandedLiveRunIdChange ?? setInternalExpandedLiveRunId;
    const setResolvedAutoExpandDismissedRunId =
        onAutoExpandDismissedRunIdChange ?? setInternalAutoExpandDismissedRunId;
    const resolvedActionStateEntries = actionStateEntries ?? entries;
    const dynamicWidgetActionStateResolver = useCallback(
        (
            widgetId: string,
            shiftIds: ReadonlyArray<string>,
            suggestedCaregiverIds?: ReadonlyArray<string>,
        ) =>
            getDynamicWidgetActionStateForShifts(
                widgetId,
                shiftIds,
                resolvedActionStateEntries,
                suggestedCaregiverIds,
            ),
        [resolvedActionStateEntries],
    );

    // Auto-expand a run while its projected outreach progress is active.
    const runIdsWithProjectedPlan = useMemo(() => {
        const result = new Set<string>();
        if (!showGeneralOutreachProgressCards) {
            return result;
        }
        for (const entry of entries) {
            if (entry.kind !== 'completed-run') {
                continue;
            }
            const runNodes = entry.run.orderedNodeIds.flatMap((nodeId) => {
                const node = entry.run.nodesById[nodeId];
                return node ? [node] : [];
            });
            for (const node of runNodes) {
                if (node.kind !== 'tool') {
                    continue;
                }
                const hasDeclaredPlan =
                    showGeneralOutreachProgressCards &&
                    node.toolName === EXECUTION_PLAN_TOOL_NAME &&
                    hasDeclaredExecutionPlanItems(node);
                const projectedStep = getProjectedToolStep({
                    toolName: node.toolName,
                    toolArguments: node.arguments,
                });
                const hasProjectedStep =
                    projectedStep !== null && showGeneralOutreachProgressCards;
                if (hasDeclaredPlan || hasProjectedStep) {
                    result.add(entry.run.runId);
                    break;
                }
            }
        }
        return result;
    }, [entries, showGeneralOutreachProgressCards]);
    const pendingApprovalEntries = useMemo(
        () => entries.filter(isPendingApprovalToolExecutionEntry),
        [entries],
    );
    const firstPendingApprovalEntryId = pendingApprovalEntries[0]?.id ?? null;
    const interstitialAssistantMessageIds = useMemo(
        () =>
            groupActivityEntries
                ? getInterstitialAssistantMessageIds(
                      entries,
                      dynamicWidgetsEnabled,
                  )
                : EMPTY_INTERSTITIAL_MESSAGE_IDS,
        [dynamicWidgetsEnabled, entries, groupActivityEntries],
    );

    // A batch's "Start Outreach" prompt is an open question to the
    // coordinator; once they say anything else to the agent, the older
    // prompt is stale and hides. Any decision-list entry that sits before
    // the latest user message loses its prompt. Among the fresh decision
    // lists (a multi-shift fan-out renders one per shift), only the last
    // one carries the shared prompt, so it lands at the bottom of the
    // group once every batch has finished.
    const {
        staleOutreachPromptEntryIds,
        outreachPromptTailEntryId,
        hasPendingCaregiverSelection,
    } = useMemo(() => {
        const stale = new Set<string>();
        let lastUserMessageIndex = -1;
        for (let i = entries.length - 1; i >= 0; i--) {
            const candidate = entries[i];
            if (candidate?.kind === 'message' && candidate.role === 'user') {
                lastUserMessageIndex = i;
                break;
            }
        }
        let tailId: string | null = null;
        let pendingCaregiverSelection = false;
        for (let i = 0; i < entries.length; i++) {
            const candidate = entries[i];
            if (i >= lastUserMessageIndex) {
                if (
                    candidate?.kind === 'tool-execution' &&
                    !candidate.isComplete &&
                    isCaregiverDecisionToolName(candidate.name)
                ) {
                    pendingCaregiverSelection = true;
                } else if (
                    candidate?.kind === 'completed-run' &&
                    hasIncompleteCaregiverDecisionTool(candidate.run)
                ) {
                    pendingCaregiverSelection = true;
                }
            }
            const rendersDecisionList =
                candidate?.kind === 'recommendation-batch' ||
                (candidate?.kind === 'subagent-result' &&
                    parseRecommendationBatchAggregate(
                        candidate.finalAssistantText,
                    ) !== null);
            if (!rendersDecisionList) {
                continue;
            }
            if (i < lastUserMessageIndex) {
                stale.add(candidate.id);
            } else {
                tailId = candidate.id;
            }
        }
        return {
            staleOutreachPromptEntryIds: stale,
            outreachPromptTailEntryId: tailId,
            hasPendingCaregiverSelection: pendingCaregiverSelection,
        };
    }, [entries]);
    // Nano fan-out does not reveal a shift's decision list until that shift's
    // recommendation call returns. While the parent run is active, the lists
    // rendered so far are therefore not a reliable count of the whole group.
    // Wait for the turn to settle before allowing the tail list to prompt.
    const shouldDeferStartOutreachPrompt =
        showCaregiverDecisionProgress &&
        (hasPendingCaregiverSelection ||
            hasIncompleteCaregiverDecisionTool(streamOverlayRun) ||
            streamOverlayRun?.status === 'running' ||
            (shouldUsePhoebeEventRead
                ? currentRunEventStreamIsGenerating
                : isActiveRunWithoutStream));

    const renderEntry = (entry: RenderTimelineEntry, index: number) => {
        const isLastEntry = index === entries.length - 1;
        const isActiveThought =
            entry.kind === 'thought' && isLastEntry && agentThinking;
        switch (entry.kind) {
            case 'completed-run': {
                const isExecutionBlock = entry.isExecutionBlock === true;
                const hasRunTreeContent = hasRenderableLiveRunTreeContent(
                    entry.run,
                );
                const hasVisibleGeneralProgressContent =
                    showGeneralOutreachProgressCards
                        ? hasVisibleGeneralProgressRunContent(entry.run)
                        : hasRunTreeContent;
                const canToggleRunTree =
                    hasRunTreeContent &&
                    (!isExecutionBlock || entry.run.status !== 'running');
                const wrapperContainsPlanCall = runIdsWithProjectedPlan.has(
                    entry.run.runId,
                );
                const showProjectedMilestonesForRun =
                    showGeneralOutreachProgressCards;
                const isExpanded =
                    hasRunTreeContent &&
                    (wrapperContainsPlanCall
                        ? // While the run is running, auto-expand so
                          // the execution plan is visible. Once the
                          // run completes, auto-collapse: the card is
                          // tucked under the "Worked for Xs ⌄" wrapper
                          // chevron — the user can click to inspect.
                          // User toggles override either default.
                          entry.run.status === 'running'
                            ? resolvedAutoExpandDismissedRunId !==
                              entry.run.runId
                            : resolvedExpandedLiveRunId === entry.run.runId
                        : isExecutionBlock
                          ? entry.run.status === 'running' ||
                            resolvedExpandedLiveRunId === entry.run.runId
                          : resolvedExpandedLiveRunId === entry.run.runId ||
                            (entry.defaultExpanded === true &&
                                resolvedAutoExpandDismissedRunId !==
                                    entry.run.runId));
                const isActiveInProgress =
                    isLastEntry &&
                    (streamOverlayRun?.status === 'running' ||
                        (shouldUsePhoebeEventRead
                            ? currentRunEventStreamIsGenerating
                            : isActiveRunWithoutStream));
                const label =
                    entry.labelOverride ??
                    (isActiveInProgress && !isPriorAssistantMessageAnimating
                        ? 'Thinking'
                        : 'Thought');
                const timelineLabel =
                    toolCardPresentation === 'minimal' && label === 'Thought'
                        ? 'Agent activity'
                        : label;
                const executionLiveStatus =
                    isExecutionBlock && entry.run.status === 'running'
                        ? (manualLiveStatus ??
                          deriveLiveStatusFromRun(entry.run))
                        : null;
                // Treat the entry as live whenever the upstream
                // signals it's active, even if the trace rebuild
                // hasn't flipped entry.run.status to 'running' yet.
                // Without this fallback, the wrapper falls into the
                // LiveStatusRow "Thinking" path for the first
                // render frame after submit, then swaps to the
                // Phoebe-logo shimmer once the stream events land
                // — visible as a one-frame "thinking" flash.
                const showInflightGeneralAgentShimmer =
                    (showProjectedMilestonesForRun ||
                        showInlineWorkflowActivitySurface) &&
                    (isActiveInProgress ||
                        (entry.run.status === 'running' &&
                            hasVisibleGeneralProgressContent));
                const generalAgentShimmerLabel = showInflightGeneralAgentShimmer
                    ? (derivePhoebeShimmerLabel(
                          entry.run,
                          showCaregiverDecisionProgress,
                      ) ?? 'Thinking')
                    : null;
                if (showInflightGeneralAgentShimmer) {
                    return (
                        <div key={entry.id} className="space-y-2">
                            {generalAgentShimmerLabel ? (
                                <PhoebeShimmerStatus
                                    label={generalAgentShimmerLabel}
                                    tip={derivePhoebeThinkingTip({
                                        enabled: showThinkingTips,
                                        isWorkflow:
                                            shouldShowWorkflowActivitySurface,
                                        run: entry.run,
                                        nowMs: timelineNowMs,
                                        tipIndex: index,
                                    })}
                                />
                            ) : null}
                            <LiveRunTree
                                run={entry.run}
                                navigateOnDraftedOutreach={true}
                                showDurationLabels={
                                    entry.showDurationLabels ?? true
                                }
                                hideLastResponseNode={
                                    entry.hideLastResponseNode ?? false
                                }
                                animatedReasoningNodeId={
                                    latestAnimatedThoughtId
                                }
                                animatedResponseNodeId={
                                    latestAnimatedAssistantMessageId
                                }
                                onTypewriterProgress={onTypewriterProgress}
                                onCaregiverClick={onCaregiverClick}
                                onClientClick={onClientClick}
                                showGeneralOutreachProgressCards={
                                    showGeneralOutreachProgressCards
                                }
                                hideTraceTitles={
                                    showProjectedMilestonesForRun ||
                                    showInlineWorkflowActivitySurface
                                }
                                subagentDraftProgress={subagentDraftProgress}
                            />
                        </div>
                    );
                }
                if (
                    showGeneralOutreachProgressCards &&
                    !hasVisibleGeneralProgressContent &&
                    !entry.labelOverride &&
                    !isActiveInProgress
                ) {
                    return null;
                }
                return (
                    <div key={entry.id} className="space-y-2">
                        <LiveStatusRow
                            status={{
                                phase: 'thinking',
                                label: timelineLabel,
                                detail: null,
                                toolName: null,
                            }}
                            isExpandable={canToggleRunTree}
                            isExpanded={isExpanded}
                            onToggle={
                                canToggleRunTree
                                    ? () => {
                                          const isCollapsingCurrentRun =
                                              isExpanded;
                                          setResolvedExpandedLiveRunId(
                                              isCollapsingCurrentRun
                                                  ? null
                                                  : entry.run.runId,
                                          );
                                          // Only touch the dismissal
                                          // slot when it's about THIS
                                          // run — clearing it on every
                                          // expand would un-dismiss
                                          // other runs the user
                                          // already collapsed.
                                          if (isCollapsingCurrentRun) {
                                              setResolvedAutoExpandDismissedRunId(
                                                  entry.run.runId,
                                              );
                                          } else if (
                                              resolvedAutoExpandDismissedRunId ===
                                              entry.run.runId
                                          ) {
                                              setResolvedAutoExpandDismissedRunId(
                                                  null,
                                              );
                                          }
                                      }
                                    : undefined
                            }
                            showSpinner={
                                !isExecutionBlock &&
                                (entry.showSpinner === true ||
                                    (!entry.labelOverride &&
                                        isActiveInProgress))
                            }
                            presentation={
                                toolCardPresentation === 'minimal'
                                    ? 'workbench'
                                    : 'default'
                            }
                            tip={derivePhoebeThinkingTip({
                                enabled: showThinkingTips,
                                isWorkflow: shouldShowWorkflowActivitySurface,
                                run: entry.run,
                                nowMs: timelineNowMs,
                                tipIndex: index,
                            })}
                        />
                        {isExpanded ? (
                            <div className="space-y-2">
                                <LiveRunTree
                                    run={entry.run}
                                    navigateOnDraftedOutreach={true}
                                    showDurationLabels={
                                        entry.showDurationLabels ?? true
                                    }
                                    hideLastResponseNode={
                                        entry.hideLastResponseNode ?? false
                                    }
                                    animatedReasoningNodeId={
                                        latestAnimatedThoughtId
                                    }
                                    animatedResponseNodeId={
                                        latestAnimatedAssistantMessageId
                                    }
                                    onTypewriterProgress={onTypewriterProgress}
                                    onCaregiverClick={onCaregiverClick}
                                    showGeneralOutreachProgressCards={
                                        showGeneralOutreachProgressCards
                                    }
                                    hideTraceTitles={
                                        showProjectedMilestonesForRun ||
                                        showInlineWorkflowActivitySurface
                                    }
                                    subagentDraftProgress={
                                        subagentDraftProgress
                                    }
                                />
                                {executionLiveStatus ? (
                                    <LiveStatusRow
                                        status={executionLiveStatus}
                                        showSpinner={true}
                                        tip={derivePhoebeThinkingTip({
                                            enabled: showThinkingTips,
                                            isWorkflow:
                                                shouldShowWorkflowActivitySurface,
                                            run: entry.run,
                                            nowMs: timelineNowMs,
                                            tipIndex: index,
                                        })}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                );
            }
            case 'message':
                if (entry.hiddenFromTranscript === true) {
                    return null;
                }
                if (interstitialAssistantMessageIds.has(entry.id)) {
                    // Interstitial narration is thinking text; training mode
                    // never shows it (see ThoughtEntry's identical rule).
                    if (showTrainingModeQuestionContext) {
                        return null;
                    }
                    return (
                        <InterstitialThinkingText
                            key={entry.id}
                            content={entry.content}
                        />
                    );
                }
                return (
                    <MessageBubble
                        key={entry.id}
                        message={entry}
                        entries={entries}
                        animateContent={
                            entry.id === latestAnimatedAssistantMessageId
                        }
                        onTypewriterProgress={onTypewriterProgress}
                        onCaregiverClick={onCaregiverClick}
                        onClientClick={onClientClick}
                        onAssistantPromptAction={onAssistantPromptAction}
                        onRetryAfterRunError={onRetryAfterRunError}
                        isRetryDisabled={isRetryDisabled}
                        shiftSuggestionTabs={
                            entry.turnSequence ===
                            shiftSuggestionTabs?.turnSequence
                                ? shiftSuggestionTabs
                                : null
                        }
                        shiftActionPolicy={shiftActionPolicy}
                        presentation={toolCardPresentation}
                        renderMessageArtifacts={renderMessageArtifacts}
                        renderAssistantMessageActions={
                            renderAssistantMessageActions
                        }
                        renderMessageFeedback={renderMessageFeedback}
                        highlightedAgentItemId={highlightedAgentItemId}
                    />
                );
            case 'compaction-marker':
                if (!debugPanelOpen) {
                    return null;
                }
                return <CompactionMarker key={entry.id} entry={entry} />;
            case 'debug-event':
                return <DebugEventMarker key={entry.id} entry={entry} />;
            case 'debug-event-group':
                return <DebugEventGroupMarker key={entry.id} entry={entry} />;
            case 'subagent-result':
                return (
                    <SubagentResultMarker
                        key={entry.id}
                        canOpenSubagentRun={canOpenSubagentRun}
                        entry={entry}
                        recommendationOrganizationId={
                            recommendationOrganizationId
                        }
                        offerStartOutreach={
                            !shouldDeferStartOutreachPrompt &&
                            !staleOutreachPromptEntryIds.has(entry.id)
                        }
                        groupPromptSlot={entry.id === outreachPromptTailEntryId}
                        onOpenSubagentRun={onOpenSubagentRun}
                        showRecommendationTraceProgress={
                            showRecommendationTraceProgress
                        }
                    />
                );
            case 'recommendation-batch':
                return (
                    <div key={entry.id} className="relative ml-7 min-w-0">
                        <PhoebeAgentMark className="absolute top-2 -left-7 size-5" />
                        <RecommendationDecisionCarousel
                            batchId={entry.batchId}
                            candidateCount={entry.candidateCount}
                            aggregateDecisions={entry.aggregateDecisions}
                            organizationIdOverride={
                                recommendationOrganizationId
                            }
                            onCaregiverClick={onCaregiverClick}
                            offerStartOutreach={
                                !shouldDeferStartOutreachPrompt &&
                                !staleOutreachPromptEntryIds.has(entry.id)
                            }
                            groupPromptSlot={
                                entry.id === outreachPromptTailEntryId
                            }
                            showTraceProgress={showRecommendationTraceProgress}
                        />
                    </div>
                );
            case 'update-playbook-artifact':
                return (
                    <UpdatePlaybookArtifactCard
                        key={entry.id}
                        artifact={entry.artifact}
                    />
                );
            case 'save-note-artifact':
                return (
                    <SaveNoteArtifactCard
                        key={entry.id}
                        artifact={entry.artifact}
                        staggerIndex={entry.staggerIndex ?? 0}
                    />
                );
            case 'run-debrief-acknowledgement':
                return (
                    <RunDebriefAcknowledgement
                        key={entry.id}
                        notes={entry.notes}
                    />
                );
            case 'structured-report-artifact':
                return (
                    <div key={entry.id} className="flex justify-start">
                        <div className="w-full max-w-md">
                            <StructuredReportArtifactPill
                                artifact={{
                                    id: entry.id,
                                    toolName: entry.toolName,
                                    title: humanizeReportToolName(
                                        entry.toolName,
                                    ),
                                    columns: entry.columns,
                                    rows: entry.rows,
                                    summary: entry.summary,
                                }}
                            />
                        </div>
                    </div>
                );
            case 'activity':
                if (!debugPanelOpen && !entry.visibleInMainTranscript) {
                    return null;
                }

                return (
                    <LifecycleChip
                        key={entry.id}
                        entry={entry}
                        onRetryAfterRunError={onRetryAfterRunError}
                        isRetryDisabled={isRetryDisabled}
                    />
                );
            case 'tool-execution':
                if (isSilentInternalToolCall(entry.name, entry.arguments)) {
                    return null;
                }
                if (isUserQuestionToolEntry(entry)) {
                    return (
                        <UserQuestionCard
                            key={entry.id}
                            entry={entry}
                            resolved={entry.isComplete}
                            isSubmitting={
                                entry.callId
                                    ? (answeringQuestionCallIds[entry.callId] ??
                                      false)
                                    : false
                            }
                            onSubmit={
                                entry.callId &&
                                onQuestionAnswer &&
                                !entry.isComplete
                                    ? async (payload) => {
                                          await onQuestionAnswer(
                                              entry.callId ?? '',
                                              payload,
                                          );
                                      }
                                    : undefined
                            }
                            onCancel={
                                entry.callId &&
                                onQuestionAnswer &&
                                !entry.isComplete
                                    ? async () => {
                                          await onQuestionAnswer(
                                              entry.callId ?? '',
                                              { cancelled: true },
                                          );
                                      }
                                    : undefined
                            }
                            presentation={toolCardPresentation}
                            showTrainingModeContext={
                                showTrainingModeQuestionContext &&
                                isTeachPhoebeUserQuestion(entry)
                            }
                            trainingModeContextReady={trainingModeContextReady}
                        />
                    );
                }
                if (isSurfaceSettingToolEntry(entry)) {
                    if (!entry.isComplete || !isRecord(entry.output)) {
                        return null;
                    }
                    return (
                        <SurfaceOrganizationSettingCard
                            key={entry.id}
                            data={entry.output}
                        />
                    );
                }
                if (entry.status === 'pending_approval' && !entry.isComplete) {
                    if (entry.id !== firstPendingApprovalEntryId) {
                        return null;
                    }
                    return (
                        <ToolApprovalQueueCard
                            key="tool-approval-queue"
                            entries={pendingApprovalEntries}
                            decidingCallIds={decidingCallIds}
                            onDecision={onToolDecision}
                            presentation={toolCardPresentation}
                            organizationName={organizationName}
                        />
                    );
                }
                return (
                    <ToolExecutionCard
                        key={entry.id}
                        entry={entry}
                        // Inside the grouped activity dropdown every row
                        // shares one left edge; the nested layout drops the
                        // top-level ml-7 gutter offset.
                        nested={groupActivityEntries}
                        showActiveLabel={isLastEntry && agentThinking}
                        onCaregiverClick={onCaregiverClick}
                        onClientClick={onClientClick}
                        showGeneralOutreachProgress={
                            showGeneralOutreachProgressCards
                        }
                        renderToolArtifacts={renderToolArtifacts}
                        renderToolOutput={renderToolOutput}
                        onInspectToolEntry={onInspectToolEntry}
                        isInspected={
                            selectedToolEntryId ===
                            getToolEntryInspectionId(entry)
                        }
                        presentation={toolCardPresentation}
                        useRawToolName={groupActivityEntries}
                    />
                );
            case 'thought':
                return (
                    <ThoughtTraceCard
                        key={entry.id}
                        entry={entry}
                        isActive={isActiveThought}
                        animateContent={entry.id === latestAnimatedThoughtId}
                        onTypewriterProgress={onTypewriterProgress}
                        decidingCallIds={decidingCallIds}
                        answeringQuestionCallIds={answeringQuestionCallIds}
                        showDurationLabels={shouldShowWorkflowActivitySurface}
                        onDecision={onToolDecision}
                        onQuestionAnswer={onQuestionAnswer}
                        onCaregiverClick={onCaregiverClick}
                        onClientClick={onClientClick}
                        showGeneralOutreachProgressCards={
                            showGeneralOutreachProgressCards
                        }
                        subagentDraftProgress={subagentDraftProgress}
                        renderToolArtifacts={renderToolArtifacts}
                        renderToolOutput={renderToolOutput}
                        onInspectToolEntry={onInspectToolEntry}
                        selectedToolEntryId={selectedToolEntryId}
                        toolCardPresentation={toolCardPresentation}
                        organizationName={organizationName}
                        showTrainingModeQuestionContext={
                            showTrainingModeQuestionContext
                        }
                        trainingModeContextReady={trainingModeContextReady}
                        useRawToolNames={groupActivityEntries}
                        showPlainTextContent={groupActivityEntries}
                    />
                );
            default:
                return null;
        }
    };

    if (!groupActivityEntries) {
        return (
            <DynamicWidgetPromptActionContext.Provider
                value={onAssistantPromptAction ?? null}
            >
                <DynamicWidgetActionStateContext.Provider
                    value={dynamicWidgetActionStateResolver}
                >
                    <RecommendationFanoutGroupProvider>
                        {entries.map((entry, index) =>
                            renderEntry(entry, index),
                        )}
                    </RecommendationFanoutGroupProvider>
                </DynamicWidgetActionStateContext.Provider>
            </DynamicWidgetPromptActionContext.Provider>
        );
    }

    const segments = buildActivityTimelineSegments(
        entries,
        interstitialAssistantMessageIds,
        dynamicWidgetsEnabled,
    );
    // The streaming turn's group must stay expanded until the turn
    // settles, even while the latest streamed entry is a narration
    // message outside the group — collapsing mid-run hides in-progress
    // work. The live group is the last group not followed by a newer
    // user message.
    const lastGroupSegmentIndex = segments.reduce(
        (lastIndex, segment, segmentIndex) =>
            segment.kind === 'group' ? segmentIndex : lastIndex,
        -1,
    );
    return (
        <DynamicWidgetPromptActionContext.Provider
            value={onAssistantPromptAction ?? null}
        >
            <DynamicWidgetActionStateContext.Provider
                value={dynamicWidgetActionStateResolver}
            >
                <RecommendationFanoutGroupProvider>
                    {segments.map((segment, segmentIndex) => {
                        if (segment.kind === 'single') {
                            return renderEntry(segment.entry, segment.index);
                        }
                        if (segment.kind === 'message-artifacts') {
                            const artifactContent =
                                renderMessageArtifacts?.(
                                    segment.entry,
                                    entries,
                                ) ?? null;
                            return artifactContent === null ? null : (
                                <div
                                    key={`message-artifacts-${segment.entry.id}`}
                                    className="ml-7 w-[calc(100%-1.75rem)] max-w-full"
                                >
                                    {artifactContent}
                                </div>
                            );
                        }
                        const lastItemIndex =
                            segment.items[segment.items.length - 1]?.index ??
                            -1;
                        const hasNewerUserMessage = entries
                            .slice(lastItemIndex + 1)
                            .some(
                                (entry) =>
                                    entry.kind === 'message' &&
                                    entry.role === 'user',
                            );
                        return (
                            <CollapsedActivityGroup
                                key={`activity-group-${segment.items[0]?.entry.id ?? 'empty'}`}
                                items={segment.items}
                                isLive={
                                    agentThinking &&
                                    segmentIndex === lastGroupSegmentIndex &&
                                    !hasNewerUserMessage
                                }
                                renderEntry={renderEntry}
                            />
                        );
                    })}
                </RecommendationFanoutGroupProvider>
            </DynamicWidgetActionStateContext.Provider>
        </DynamicWidgetPromptActionContext.Provider>
    );
}

// Interstitial assistant narration rendered inside the activity group as a
// thinking event: plain muted text, no chat bubble.
function InterstitialThinkingText({ content }: { readonly content: string }) {
    if (content.trim().length === 0) {
        return null;
    }
    return (
        <div className="text-muted-foreground w-full max-w-full text-left text-xs break-words">
            <Markdown
                className="space-y-2"
                components={{
                    p: ({ node, ...props }) => (
                        <p className="mb-2 text-xs last:mb-0" {...props} />
                    ),
                    strong: ({ node, ...props }) => (
                        <strong className="font-semibold" {...props} />
                    ),
                    em: ({ node, ...props }) => (
                        <em className="italic" {...props} />
                    ),
                }}
            >
                {content}
            </Markdown>
        </div>
    );
}

function CollapsedActivityGroup({
    items,
    isLive,
    renderEntry,
}: {
    readonly items: Array<{
        readonly entry: RenderTimelineEntry;
        readonly index: number;
    }>;
    readonly isLive: boolean;
    readonly renderEntry: (
        entry: RenderTimelineEntry,
        index: number,
    ) => React.ReactNode;
}) {
    // While the run is live the group stays open so activity streams in;
    // once it settles it collapses to the summary row unless the user has
    // explicitly toggled it.
    const [userToggledOpen, setUserToggledOpen] = useState<boolean | null>(
        null,
    );
    const isOpen = userToggledOpen ?? isLive;
    const { toolCount, thinkingCount } = summarizeActivityGroup(items);
    if (toolCount === 0 && thinkingCount === 0) {
        // Nothing visible inside (e.g. only silent internal tool calls):
        // render the entries directly so they can decide to render null.
        return (
            <React.Fragment>
                {items.map(({ entry, index }) => renderEntry(entry, index))}
            </React.Fragment>
        );
    }
    const parts: Array<string> = [];
    if (thinkingCount > 0) {
        parts.push(`${thinkingCount} thinking`);
    }
    if (toolCount > 0) {
        parts.push(`${toolCount} tool call${toolCount === 1 ? '' : 's'}`);
    }
    return (
        // ml-7 matches the assistant-message column so the whole turn —
        // activity row, dropdown content, final message — shares one left
        // edge. When expanded, the summary row and its dropdown share one
        // bounding box so they read as a single component.
        <div
            className={cn(
                'ml-7 w-[calc(100%-1.75rem)] max-w-full',
                isOpen &&
                    'rounded-[7px] border border-[#DBE3E3]/90 bg-[#F0F5F5]/45 shadow-[0_1px_0_rgba(0,36,41,0.04)]',
            )}
            data-testid="agent-activity-group"
        >
            <button
                type="button"
                data-testid="agent-activity-group-toggle"
                className={cn(
                    'text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-xs transition-colors',
                    isOpen
                        ? 'w-full cursor-pointer px-3 py-2 text-left'
                        : '-ml-1.5 rounded-sm px-1.5 py-1',
                )}
                aria-expanded={isOpen}
                onClick={() => setUserToggledOpen(!isOpen)}
            >
                <ChevronRight
                    className={cn(
                        'size-3 shrink-0 transition-transform duration-150',
                        isOpen && 'rotate-90',
                    )}
                />
                {isLive ? (
                    <span
                        className="bg-primary size-1.5 animate-pulse rounded-full"
                        aria-hidden="true"
                    />
                ) : null}
                <span className="tabular-nums">{parts.join(', ')}</span>
            </button>
            {isOpen ? (
                <div
                    className="flex flex-col gap-2 border-t border-[#DBE3E3]/70 px-3 pt-2.5 pb-3"
                    data-testid="agent-activity-group-body"
                >
                    {items.map(({ entry, index }) => renderEntry(entry, index))}
                </div>
            ) : null}
        </div>
    );
}

type PhoebeAgentEventFeedFallbackRunStatus =
    | 'idle'
    | 'running'
    | 'failed'
    | 'completed'
    | 'cancelled'
    | string
    | null;

export type PhoebeAgentEventFeedChatViewState = {
    readonly transcriptEntries: Array<TranscriptEntry>;
    readonly timelineEntries: Array<RenderTimelineEntry>;
    readonly isAgentBusy: boolean;
    readonly currentRunEventStreamIsGenerating: boolean;
    readonly hasActivePendingEvents: boolean;
    readonly shouldRefreshRunMetadata: boolean;
    readonly timelineNowMs: number;
    readonly latestAnimatedAssistantMessageId: string | null;
    readonly latestAnimatedThoughtId: string | null;
};

function deriveRunBusyFromEventFeed(
    events: Array<PhoebeAgentRunEventRead>,
): boolean | null {
    const latestLifecycleEvent = [...events]
        .reverse()
        .find((event) =>
            [
                'run_started',
                'run_completed',
                'run_interrupted',
                'run_cancelled',
                'run_failed',
            ].includes(event.type),
        );

    if (!latestLifecycleEvent) {
        return null;
    }

    return latestLifecycleEvent.type === 'run_started';
}

function hasActivePendingRunEvents(
    pendingEvents: Array<PhoebeAgentRunPendingEventRead>,
): boolean {
    return pendingEvents.some((event) => event.status === 'pending');
}

export function usePhoebeAgentEventFeedChatViewState({
    events,
    pendingEvents,
    collapseInlineDebugEvents = true,
    isSending = false,
    fallbackRunStatus = null,
    phoebeAgentRunId = null,
    shouldShowWorkflowActivitySurface = false,
    showGeneralOutreachProgressCards = false,
    renderToolExecutionsTopLevel = false,
    showInlineDebugEvents = false,
    liveEventIds = null,
}: {
    readonly events: Array<PhoebeAgentRunEventRead>;
    readonly pendingEvents: Array<PhoebeAgentRunPendingEventRead>;
    readonly collapseInlineDebugEvents?: boolean;
    readonly isSending?: boolean;
    readonly fallbackRunStatus?: PhoebeAgentEventFeedFallbackRunStatus;
    readonly phoebeAgentRunId?: string | null;
    readonly shouldShowWorkflowActivitySurface?: boolean;
    readonly showGeneralOutreachProgressCards?: boolean;
    readonly renderToolExecutionsTopLevel?: boolean;
    readonly showInlineDebugEvents?: boolean;
    // Live event IDs from the run event stream; when provided, the latest
    // live assistant message/thought animate with the typewriter effect.
    readonly liveEventIds?: ReadonlySet<string> | null;
}): PhoebeAgentEventFeedChatViewState {
    const dynamicWidgetBuildContext = useDynamicWidgetBuildContext();
    const dynamicWidgetsEnabled = dynamicWidgetBuildContext !== null;
    const eventDerivedRunBusy = useMemo(
        () => deriveRunBusyFromEventFeed(events),
        [events],
    );
    const hasActivePendingEvents = useMemo(
        () => hasActivePendingRunEvents(pendingEvents),
        [pendingEvents],
    );
    const isAgentBusy =
        isSending ||
        hasActivePendingEvents ||
        (eventDerivedRunBusy ?? fallbackRunStatus === 'running');
    const currentRunEventStreamIsGenerating = isAgentBusy;
    const shouldRefreshRunMetadata =
        isSending ||
        hasActivePendingEvents ||
        eventDerivedRunBusy === true ||
        fallbackRunStatus === 'running';
    const [timelineNowMs, setTimelineNowMs] = useState(() => Date.now());

    const transcriptEntries = useMemo(
        () =>
            adaptTranscriptForLegacy(
                buildTranscriptFromPhoebeEvents(events, pendingEvents, {
                    showInlineDebugEvents,
                    runId: phoebeAgentRunId,
                }),
            ),
        [events, pendingEvents, phoebeAgentRunId, showInlineDebugEvents],
    );
    const hasPendingFanInWork = useMemo(
        () => hasPendingRecommendationBatch(transcriptEntries),
        [transcriptEntries],
    );

    useEffect(() => {
        // Keep the work clock running while hidden fork evaluators are
        // still deciding, even though the parent run itself is paused.
        if (!isAgentBusy && !hasPendingFanInWork) {
            return;
        }

        setTimelineNowMs(Date.now());
        const intervalId = window.setInterval(() => {
            setTimelineNowMs(Date.now());
        }, 1000);
        return () => window.clearInterval(intervalId);
    }, [hasPendingFanInWork, isAgentBusy]);
    const timelineEntries = useMemo(
        () =>
            buildPhoebeAgentRenderedTimelineEntries({
                displayedTranscript: transcriptEntries,
                collapseInlineDebugEvents,
                currentRunEventStreamIsGenerating,
                dynamicWidgetBuildContext,
                dynamicWidgetsEnabled,
                isAgentBusy,
                phoebeAgentRunId,
                shouldShowWorkflowActivitySurface,
                showGeneralOutreachProgressCards,
                renderToolExecutionsTopLevel,
                timelineNowMs,
            }),
        [
            collapseInlineDebugEvents,
            currentRunEventStreamIsGenerating,
            dynamicWidgetBuildContext,
            dynamicWidgetsEnabled,
            isAgentBusy,
            phoebeAgentRunId,
            renderToolExecutionsTopLevel,
            shouldShowWorkflowActivitySurface,
            showGeneralOutreachProgressCards,
            timelineNowMs,
            transcriptEntries,
        ],
    );

    const latestAnimatedAssistantMessageId = liveEventIds
        ? getLatestLiveAssistantMessageId({
              entries: transcriptEntries,
              liveEventIds,
              eventLayerReadEnabled: true,
          })
        : null;
    const latestAnimatedThoughtId = liveEventIds
        ? getLatestLiveThoughtId({
              entries: transcriptEntries,
              liveEventIds,
              eventLayerReadEnabled: true,
          })
        : null;

    return {
        transcriptEntries,
        timelineEntries,
        isAgentBusy,
        currentRunEventStreamIsGenerating,
        hasActivePendingEvents,
        shouldRefreshRunMetadata,
        timelineNowMs,
        latestAnimatedAssistantMessageId,
        latestAnimatedThoughtId,
    };
}

function isToolOutputRendererPayload(
    value: ReactNode | ToolOutputRendererPayload,
): value is ToolOutputRendererPayload {
    return (
        typeof value === 'object' &&
        value !== null &&
        'kind' in value &&
        value.kind === 'custom-tool-output'
    );
}

export function getToolEntryInspectionId(entry: ToolExecutionEntry): string {
    return entry.callId ?? entry.commandId ?? entry.id;
}

function formatThoughtTitle(durationMs: number | null | undefined): string {
    if (!durationMs || durationMs <= 0) {
        return 'Thought';
    }
    return `Thought • ${formatDuration(durationMs)}`;
}
